import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useEngineeringAIContext } from '../services/engineeringAIContext';
import { Button, Checkbox, message, Modal, Select, Tag } from 'antd';
import { ApartmentOutlined, CloseOutlined, CopyOutlined, DatabaseOutlined, DeleteOutlined, DoubleLeftOutlined, DoubleRightOutlined, LineChartOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import DraggableModal from '../components/common/DraggableModal';
import { useProjectStore } from '../hooks/useProjectStore';
import { formationAPI, wellboreAPI, sweetSpotAPI, fractureAPI, loggingAPI } from '../services/api';
import { useAgentCommands } from '../services/agent/registry';
import { deduplicateFractureDesigns } from '../utils/fractureDesignIdentity';
import { cleanDesignName, generatedDesignName, uniqueDesignName } from '../utils/designName';
import QuickDeployPanel, {type DeploymentMeta} from './fracture/QuickDeployPanel';
import type {PreFracStageDraft} from './fracture/preFracDesign';
import CustomStagePanel from './fracture/CustomStagePanel';
import ClusterDesignPanel, {type ClusterDesignMeta,type ClusterDesignPanelHandle} from './fracture/ClusterDesignPanel';
import StageDesignResultPanel from './fracture/StageDesignResultPanel';
import { setEngineeringDesignContext } from './fracture/fractureEngineering';
import { simulationHandoffUrl } from './fracsim/designSimulationHandoff';
import {
  loadSweetSpotEvaluationResult,
  useSweetSpotWorkspaceStore,
} from './sweetspot/sweetSpotWorkspaceStore';
import { getActiveRunId, loadActiveEvaluationRun } from './sweetspot/sweetSpotEvaluationStorage';
import { adaptEvaluationRunToLegacyResult } from './sweetspot/sweetSpotEvaluationAdapter';
import type { SweetSpotEvaluationResult } from './sweetspot/types';
import {
  buildScoringTemplate,
  scoreFracPlan,
  scoreStageCluster,
  type ReservoirType,
  type ScoringConfig,
  type StageCheck,
} from '../utils/fractureScoring';

// ═══════════════════════════════════════════════
// FracSight 压裂设计 — 按甜点自动设计 / 导入方案
// ═══════════════════════════════════════════════

const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.06)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  headerBg: '#FFFFFF', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  statBorder: '#ECEEF1', target: '#8B3A2A', stage: '#2874A6',
};
const COMPARE_COLORS = [
  { head: '#E8F3FA', soft: '#F3F9FC', text: '#246A92' },
  { head: '#FFF0E8', soft: '#FFF8F3', text: '#A94B30' },
  { head: '#EAF6EF', soft: '#F4FAF6', text: '#28734B' },
];
const IMPORTANT_COMPARE_ROWS = new Set(['方案评分','段数','排量','总液量','总砂量']);
const compareMetricHead: React.CSSProperties = {position:'sticky',top:0,zIndex:3,width:108,padding:'7px 9px',textAlign:'left',background:'#EEF2F5',color:'#526578',borderBottom:'1px solid #D8DEE5',fontWeight:700};
const comparePlanHead: React.CSSProperties = {position:'sticky',top:0,zIndex:2,padding:'7px 9px',textAlign:'left',borderLeft:'1px solid #D7DEE5',borderBottom:'1px solid #C9D1D9',overflow:'hidden'};
const compareMetricCell: React.CSSProperties = {padding:'6px 9px',height:31,color:'#607184',fontWeight:600,whiteSpace:'nowrap',borderBottom:'1px solid #E7EBEF',background:'#F7F9FA'};
const compareValueCell: React.CSSProperties = {padding:'6px 9px',height:31,color:'#243445',borderLeft:'1px solid #E1E6EB',borderBottom:'1px solid #E7EBEF',verticalAlign:'middle',overflow:'hidden'};
const ellipsisOneLine: React.CSSProperties = {display:'block',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'};
const fullAdviceText: React.CSSProperties = {display:'block',whiteSpace:'normal',lineHeight:1.55,overflowWrap:'anywhere',padding:'2px 0'};
const analogTh: React.CSSProperties = {padding:'5px 7px',background:'#EEF2F5',color:'#526578',border:'1px solid #DDE3E8',textAlign:'left'};
const analogTd: React.CSSProperties = {padding:'5px 7px',border:'1px solid #E4E8EC',color:'#40556A'};
const formatAnalogValue=(value:string|number|null)=>value==null||value===''?'—':typeof value==='number'?Number(value.toFixed(3)):value;
const GRADE: Record<string, { label: string; color: string; bg: string }> = {
  I:   { label: 'Ⅰ', color: '#1E8449', bg: 'rgba(30,132,73,0.16)' },
  II:  { label: 'Ⅱ', color: '#2874A6', bg: 'rgba(40,116,166,0.14)' },
  III: { label: 'Ⅲ', color: '#D4770C', bg: 'rgba(212,119,12,0.14)' },
  IV:  { label: 'Ⅳ', color: '#C0392B', bg: 'rgba(192,57,43,0.12)' },
};
const LITH_ZH: Record<string, string> = {
  sandstone: '砂岩', shale: '页岩', limestone: '灰岩', mudstone: '泥岩',
  dolomite: '白云岩', conglomerate: '砾岩', coal: '煤层', other: '其他',
};
const LITH_COLOR: Record<string, string> = {
  砾岩: '#C4A87A', 灰岩: '#9CAAB8', 白云岩: '#A8C4A8',
  泥岩: '#7A8C72', 页岩: '#5A7050', 砂岩: '#D4B886', 煤层: '#2A2A2A', 其他: '#707078',
};

type Layer = { name: string; top: number; bottom: number; color: string; target?: boolean };
type Station = {
  md: number;
  inc: number;
  azi: number;
  tvd: number;
  ns: number | null;
  ew: number | null;
};
type Eval = { top: number; bottom: number; grade: string; score: number };
type LogInterp = {
  top_depth: number;
  bottom_depth: number;
  lithology?: string;
  porosity?: number | null;
  permeability?: number | null;
  water_saturation?: number | null;
  oil_saturation?: number | null;
  fluid_type?: string;
};
type Stage = {
  id: number;
  stage_number: number; top_perf: number; bottom_perf: number;
  fluid_volume: number; proppant_mass: number;
  schedule: StageScheduleStep[];
};
type StageScheduleStep = {
  step?: string; step_type?: string; stage_index?: number | null;
  fluid_m3?: number; volume?: number; sand_conc?: number; sand_concentration?: number;
  proppant_t?: number; sand_amount?: number; rate: number;
};
type ClusterSpacingSummary = {
  average_m: number;
  minimum_m: number;
  maximum_m: number;
  adjacent_spacings_m: number[];
};
type ClusterSpacingBasis = {
  method: string;
  display_name: string;
  design_principle: string;
  numeric_value_source: string;
  numeric_value_notice: string;
  calibration_status: string;
  lateral_length_m?: number;
  lateral_class_label?: string;
  recommended_spacing_m?: [number, number];
  range_method?: string;
  references: { code: string; name: string; role: string; status: string }[];
};
type AnalogCandidate = {
  design_id: number;
  project_id: number;
  well_name: string;
  target_formation: string;
  design_name: string;
  stage_count: number;
  validation_status: string;
  verified: boolean;
  similarity_score: number;
  data_completeness: number;
  distance_km: number | null;
  recommendation: string;
  feature_components: Array<{key:string;label:string;target:string|number|null;source:string|number|null;delta?:number;similarity:number|null;status:string}>;
  transform: {geometry_scale:number;cluster_scale:number;fluid_scale:number;proppant_scale:number;rate_scale:number;matrix_order:string[];diagonal_matrix:number[]};
};
type Design = {
  id: number; name: string; stage_count: number; cluster_per_stage: number;
  cluster_spacing: number; stage_spacing: number; pump_rate: number;
  total_fluid: number; total_proppant: number; fluid_type: string;
  proppant_type: string; max_proppant_conc: number; is_active: boolean;
  created_at?: string;
  notes: string; stages: Stage[];
  design_data?: {
    mode?: string;
    lateral?: [number, number];
    stage_cluster_design?: {
      stage_number: number;
      cluster_per_stage: number;
      cluster_spacing: number;
      cluster_spacing_summary?: ClusterSpacingSummary;
      cluster_spacing_basis?: ClusterSpacingBasis;
      cluster_positions_md?: number[];
      quality_score?: number;
      quality_label?: string;
      recommended_process?: {
        stage_length_m?: number;
        cluster_count?: number;
        cluster_spacing_m?: number;
        cluster_spacing_summary?: ClusterSpacingSummary;
        cluster_spacing_basis?: ClusterSpacingBasis;
        cluster_positions_md?: number[];
        fluid_volume_m3?: number;
        proppant_mass_t?: number;
        pump_rate_m3min?: number;
        fluid_intensity_m3_per_m?: number;
        proppant_intensity_t_per_m?: number;
        max_proppant_conc_kg_m3?: number;
        average_proppant_conc_kg_m3?: number;
        perforation_layout?: string;
        perforation_holes_per_cluster?: number[];
        perforation_diameter_mm?: number;
        discharge_coefficient?: number;
        note?: string;
      };
      reason?: string;
      stress_delta_mpa?: number;
      avg_brittleness?: number;
      objective_total?: number;
      heterogeneity_cost?: number;
      length_cost?: number;
      risk_cost?: number;
      effective_reservoir_ratio?: number;
      stress_range?: number;
      missing_ratio?: number;
      reasons?: string[];
      constraint_status?: Record<string, boolean | string | number>;
      objective_features?: string[];
      stage_input_coverage?: Record<string, number>;
      objective_components?: Record<string, number>;
      risk_index?: number;
      risk_sources?: string[];
      risk_zones?: { name?: string; type?: string; severity?: number; overlap_m?: number }[];
      natural_fracture_density?: number;
      microseismic_event_count?: number;
      competition?: {
        uniformity?: number;
        dominant_cluster_ratio?: number;
        flow_share?: number[];
        warnings?: string[];
      };
      limited_entry_result?: {
        status?: string;
        total_rate_m3_min?: number;
        heel_pressure_increment_mpa?: number;
        flow_uniformity?: number;
        coefficient_of_variation?: number;
        dominant_cluster_ratio?: number;
        clusters?: {
          cluster_number: number;
          cluster_md_m: number;
          allocated_rate_m3_min: number;
          perforation_hole_count: number;
          perforation_diameter_mm: number;
          perforation_friction_mpa: number;
        }[];
      };
      optimization_candidate?: {
        method?: string;
        baseline_uniformity?: number;
        optimized_uniformity?: number;
        baseline_cv?: number;
        optimized_cv?: number;
        status?: string;
        clusters?: {
          cluster_number: number;
          allocated_rate_m3_min: number;
          perforation_hole_count: number;
          perforation_friction_mpa: number;
        }[];
      };
    }[];
    optimization_run?: {
      algorithm_version?: string;
      mode?: string;
      reservoir_model?: {
        requested_reservoir_type?: string;
        canonical_reservoir_type?: string;
        model_name?: string;
        model_version?: string;
        is_fallback?: boolean;
        reason?: string;
        data_confidence?: {
          level?: 'high' | 'medium' | 'low' | string;
          score?: number | null;
          coverage?: Record<string, number>;
          missing_key_inputs?: string[];
          proxy_inputs?: Record<string, number>;
          degradation?: string;
        };
      };
      design_explanation?: {
        summary?: string;
        limitations?: string[];
        weight_source?: string;
        weight_notice?: string;
      };
      version_record?: {
        design_schema_version?: string;
        algorithm_version?: string;
        reservoir_model_version?: string;
        reservoir_rule_template_version?: string;
        input_profile_version?: string;
        calibration_status?: string;
      };
      parameter_sources?: {
        parameter_name: string;
        coverage: number;
        source_type: string;
        calibration_status: string;
      }[];
      constraints?: Record<string, unknown>;
    };
    field_baseline_guardrails?: {
      preserved_parameters: string[];
      optimized_parameters: string[];
      stage_count: { field: number; recommended: number; deviation_pct: number };
      total_fluid_m3: { field: number; recommended: number; deviation_pct: number };
      total_proppant_t: { field: number; recommended: number; deviation_pct: number };
      max_stage_count_change?: number;
      changed_boundary_count?: number;
      all_constraints_satisfied?: boolean;
      overall_quality_score?: number;
      reason: string;
    };
    average_proppant_concentration_kg_m3?: number;
    version_record?: Record<string, string>;
  };
};

type FrameworkStatus = {
  overall_readiness: number;
  data_readiness: {
    key: string;
    label: string;
    layer: string;
    count: number;
    ready: boolean;
    status: string;
    required_for: string[];
  }[];
  roadmap: {
    key: string;
    title: string;
    goal: string;
    deliverable: string;
    status: string;
  }[];
  model_registry: {
    key: string;
    label: string;
    status: string;
    inputs: string[];
  }[];
  calibration_defaults: Record<string, {
    default: number;
    status: string;
    calibrate_with: string[];
  }>;
  active_design: {
    id: number | null;
    name: string;
    competition_model?: { version?: string; description?: string };
    coupled_data_sources?: Record<string, number>;
  };
  next_actions: string[];
};

type FormationInputs = {
  version: string;
  reservoir_type: ReservoirType;
  input_counts: Record<string, number>;
  coverage: Record<string, { count: number; total: number; coverage: number; ready: boolean }>;
};

const buildFallbackFramework = (): FrameworkStatus => ({
  overall_readiness: 38,
  data_readiness: [
    { key: 'survey', label: '井轨迹/测斜', layer: '数据接入', count: 0, ready: true, status: '已接入', required_for: ['自动分段', '风险图层', '裂缝模拟'] },
    { key: 'sweetspot', label: '甜点评价', layer: '数据接入', count: 0, ready: true, status: '已接入', required_for: ['分段分簇', '区块校正'] },
    { key: 'mechanics', label: '岩石力学/地应力', layer: '地质力学', count: 0, ready: true, status: '已接入', required_for: ['应力差', '应力阴影', '裂缝扩展'] },
    { key: 'avoidance', label: '断层/避障区', layer: '风险图层', count: 0, ready: false, status: '待接入', required_for: ['风险约束', '单段特殊设计'] },
    { key: 'natural_fracture', label: '天然裂缝/缝密度', layer: '风险图层', count: 0, ready: false, status: '待接入', required_for: ['复杂缝网', '风险约束'] },
    { key: 'microseismic', label: '微地震', layer: '监测反馈', count: 0, ready: false, status: '待接入', required_for: ['改造体积验证', '模型反演'] },
    { key: 'fracture_curve', label: '压裂施工曲线', layer: '施工反馈', count: 0, ready: false, status: '待接入', required_for: ['压力拟合', '限流射孔校正'] },
    { key: 'production', label: '生产动态', layer: '生产反馈', count: 0, ready: false, status: '待接入', required_for: ['权重标定', '闭环优化'] },
  ],
  roadmap: [
    { key: 'data_coupling', title: '1. 数据接入完善', goal: '统一接入甜点、地应力、断层、天然裂缝、微地震、施工曲线和生产动态。', deliverable: '风险图层和数据准备度看板', status: 'active' },
    { key: 'block_calibration', title: '2. 区块参数校正', goal: '用本区块历史井校正应力差阈值、风险权重、簇距下限和液砂强度。', deliverable: '区块模板和权重标定接口', status: 'planned' },
    { key: 'cluster_competition', title: '3. 多簇竞争模型', goal: '从规则型均匀性评分升级到射孔摩阻、孔眼数、应力阴影和近井摩阻耦合。', deliverable: '簇间进液比例和限流射孔建议', status: 'active' },
    { key: 'fracture_propagation', title: '4. 裂缝扩展模拟', goal: '引入 PKN/KGD/拟三维模型，计算缝长、缝高、缝宽、净压力和支撑剂铺置。', deliverable: '单段/全井裂缝几何预测', status: 'planned' },
    { key: 'geomechanics_coupling', title: '5. 地质力学耦合', goal: '耦合三维地应力、天然裂缝、层理、断层和邻井干扰。', deliverable: '地质工程一体化优化器', status: 'planned' },
    { key: 'closed_loop', title: '6. 历史数据闭环优化', goal: '用施工、微地震和生产结果反演每段每簇贡献，自动更新下一轮设计权重。', deliverable: '设计-施工-监测-生产闭环', status: 'planned' },
  ],
  model_registry: [
    { key: 'rule_based_stage_cluster', label: '规则型段簇检查', status: 'active', inputs: ['sweetspot', 'mechanics'] },
    { key: 'risk_layer_constraint', label: '风险图层约束', status: 'active', inputs: ['avoidance', 'natural_fracture', 'microseismic', 'production'] },
    { key: 'rule_based_cluster_competition', label: '简化多簇竞争', status: 'active', inputs: ['sweetspot', 'mechanics', 'risk_layer_constraint'] },
    { key: 'limited_entry_competition', label: '限流射孔/簇间分流', status: 'planned', inputs: ['fracture_curve', 'perforation', 'fluid_rheology'] },
    { key: 'pkn_kgd_pseudo3d', label: 'PKN/KGD/拟三维裂缝扩展', status: 'planned', inputs: ['mechanics', 'fluid', 'proppant', 'schedule'] },
    { key: 'history_match_closed_loop', label: '历史拟合闭环优化', status: 'planned', inputs: ['microseismic', 'fracture_curve', 'production'] },
  ],
  calibration_defaults: {
    stress_split_mpa: { default: 4.0, status: '经验默认', calibrate_with: ['施工压力异常', '微地震改造范围', '压后产量'] },
    risk_weight: { default: 0.35, status: '经验默认', calibrate_with: ['断层邻近段效果', '压窜/出水记录', '微地震异常'] },
    competition_uniformity_limit: { default: 0.72, status: '经验默认', calibrate_with: ['簇效率解释', '限流射孔试验', '施工曲线拟合'] },
  },
  active_design: {
    id: null,
    name: '当前方案',
    competition_model: { version: 'rule_based_cluster_competition_v1', description: '前端兜底显示；后端恢复后将显示真实项目统计。' },
    coupled_data_sources: {},
  },
  next_actions: [
    '优先补充断层/避障区、天然裂缝、微地震、压裂施工曲线和生产动态数据。',
    '接入压裂施工曲线后，可升级限流射孔和压力拟合。',
    '接入微地震和生产数据后，可开始区块参数校正。',
  ],
});

const interp = (arr: { x: number; y: number }[], x: number): number => {
  if (!arr.length) return x;
  if (x <= arr[0].x) return arr[0].y;
  if (x >= arr[arr.length - 1].x) return arr[arr.length - 1].y;
  for (let i = 1; i < arr.length; i++) {
    if (arr[i].x >= x) {
      const a = arr[i - 1], b = arr[i];
      const t = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x);
      return a.y + t * (b.y - a.y);
    }
  }
  return arr[arr.length - 1].y;
};

const FP_CACHE: Record<number, {
  layers: Layer[]; survey: Station[]; evals: Eval[]; logInterps: LogInterp[]; designs: Design[]; formationInputs?: FormationInputs | null;
}> = {};
const SCORING_CONFIG_KEY = 'fracture_scoring_config_v2';
const STAGE_SCORING_OVERRIDES_KEY = 'fracture_stage_scoring_overrides_v1';
const LAST_STAGE_SCORING_CONFIG_KEY = 'fracture_last_stage_scoring_config_v1';

const loadScoringConfig = (): ScoringConfig => {
  const fallback: ScoringConfig = { reservoirType: 'shale', intent: 'balanced' };
  try {
    const raw = localStorage.getItem(SCORING_CONFIG_KEY);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
};

const loadStageOverrides = (): Record<string, ScoringConfig> => {
  try {
    const raw = localStorage.getItem(STAGE_SCORING_OVERRIDES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const loadLastStageConfig = (): ScoringConfig | null => {
  try {
    const raw = localStorage.getItem(LAST_STAGE_SCORING_CONFIG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const inferReservoirFromLogs = (rows: LogInterp[], layers: Layer[]): ReservoirType => {
  const text = [
    ...rows.map((r) => r.lithology || ''),
    ...layers.map((l) => l.name || ''),
  ].join(' ').toLowerCase();
  if (/(coal|煤)/.test(text)) return 'coalbed';
  if (/(limestone|dolomite|carbonate|灰岩|白云岩|碳酸盐)/.test(text)) return 'carbonate';
  if (/(sand|conglomerate|砂|砾)/.test(text)) return 'tight_sand';
  return 'shale';
};

const OBJECTIVE_FEATURE_LABELS: Record<string, string> = {
  sweet_score: '甜点',
  porosity: '孔隙度',
  permeability: '渗透率',
  water_saturation: '含水',
  brittleness: '脆性',
  youngs_modulus: '杨氏模量',
  poissons_ratio: '泊松比',
  ucs: 'UCS',
  tensile_strength: '抗拉强度',
  sigma_h: 'σhmin',
  stress_diff: '应力差',
  risk_index: '风险',
  shale_difficulty_proxy: '压裂难度代理',
  log_permeability: '对数渗透率',
  hydrocarbon_fraction: '含烃比例',
  reservoir_coefficient: '储集系数',
  natural_fracture_density: '天然裂缝密度',
  natural_fracture_connectivity: '天然裂缝连通性',
  vug_density: '孔洞密度',
  vug_size: '孔洞尺度',
  fault_risk: '断层风险',
  leakoff_risk: '漏失风险',
  water_risk: '含水风险',
  turn_risk: '转向风险',
  frac_hit_risk: '压窜风险',
  nf_connect_benefit: '裂缝连通收益',
  vug_connect_benefit: '孔洞连通收益',
  gas_content: '含气量',
  coal_indicator: '有效煤层',
  cleat_density: '割理密度',
  cleat_connectivity: '割理连通性',
  cleat_orientation_factor: '割理方位因子',
  cleat_connect_benefit: '割理连通收益',
  coal_structure_index: '煤体结构',
  parting_thickness: '夹矸厚度',
  roof_floor_risk: '顶底板风险',
  fines_risk: '煤粉风险',
  collapse_risk: '坍塌风险',
  damage_risk: '排采伤害风险',
  quality_penalty: '质量惩罚',
  reservoir_quality_penalty: '储层质量惩罚',
  risk_penalty: '风险惩罚',
  gas_quality_penalty: '含气质量惩罚',
  nf_connectivity_benefit: '裂缝连通收益',
  vug_connectivity_benefit: '孔洞连通收益',
  cleat_connectivity_benefit: '割理连通收益',
  fines_risk_proxy_coverage: '煤粉风险代理',
};

const CONSTRAINT_LABELS: Record<string, string> = {
  min_length: '最小段长',
  max_length: '最大段长',
  stress_range: '段内应力差',
  effective_reservoir_ratio: '有效储层比例',
  missing_ratio: '数据缺失率',
  no_cut_zone: '禁止切段区',
  shale_mean_brittleness: '页岩脆性',
  shale_mean_water_saturation: '页岩含水',
  tight_sand_mean_porosity: '砂岩孔隙度',
  tight_sand_mean_permeability: '砂岩渗透率',
  tight_sand_reservoir_coefficient: '储集系数',
  carbonate_effective_ratio: '碳酸盐岩有效比例',
  carbonate_leakoff_risk: '漏失风险',
  carbonate_water_risk: '含水风险',
  carbonate_fault_risk: '断层风险',
  coalbed_effective_ratio: '有效煤层比例',
  coalbed_effective_thickness: '有效煤厚',
  coalbed_gas_content: '含气量',
  coalbed_parting_thickness: '夹矸厚度',
  coalbed_roof_floor_risk: '顶底板风险',
  coalbed_fines_risk: '煤粉风险',
  coalbed_collapse_risk: '坍塌风险',
  coalbed_water_saturation: '煤层含水',
};

const DEFAULT_OBJECTIVE_FEATURES = [
  'sweet_score',
  'porosity',
  'permeability',
  'water_saturation',
  'brittleness',
  'youngs_modulus',
  'poissons_ratio',
  'ucs',
  'tensile_strength',
  'sigma_h',
  'stress_diff',
  'risk_index',
];

const featureLabel = (key: string) => OBJECTIVE_FEATURE_LABELS[key] || key;

const designTypeLabel = (design: Design) => {
  const mode = design.design_data?.mode;
  if (mode === 'manual_import') return '人工导入';
  if (mode === 'neighbor_analog') return '邻井择选';
  if (mode === 'field_constrained_data_driven') return '类比井智能';
  if (mode === 'sample_case') return '现场实施方案';
  if (mode === 'data_driven') return '旧数据驱动';
  return '既有方案';
};

export default function FracturePage() {
  const navigate = useNavigate();
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const liveEvaluationResult = useSweetSpotWorkspaceStore((state) =>
    projectId ? state.resultByProject[projectId] : undefined);
  const [indexedEvaluationResult, setIndexedEvaluationResult] = useState<SweetSpotEvaluationResult>();
  const versionedActiveRunId = useMemo(() => {
    if (!projectId) return undefined;
    try { return getActiveRunId(projectId); } catch { return undefined; }
  }, [projectId]);
  const evaluationResult = liveEvaluationResult
    ?? indexedEvaluationResult
    ?? (versionedActiveRunId ? undefined : loadSweetSpotEvaluationResult(projectId));

  const c0 = projectId ? FP_CACHE[projectId] : undefined;
  const [layers, setLayers] = useState<Layer[]>(c0?.layers ?? []);
  const [survey, setSurvey] = useState<Station[]>(c0?.survey ?? []);
  const [evals, setEvals] = useState<Eval[]>(c0?.evals ?? []);
  const [logInterps, setLogInterps] = useState<LogInterp[]>(c0?.logInterps ?? []);
  const [designs, setDesigns] = useState<Design[]>(c0?.designs ?? []);
  const [selId, setSelId] = useState<number | null>(null);
  const [selStage, setSelStage] = useState<number | null>(null);
  const [loading, setLoading] = useState(!c0);
  const [busy, setBusy] = useState(false);
  const [quickDeployOpen, setQuickDeployOpen] = useState(false);
  const [quickDeployMode, setQuickDeployMode] = useState<'engineering'|'geometric'>('engineering');
  const [smartDeployOpen, setSmartDeployOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const draftManualOpenedRef = useRef<number | null>(null);
  const [pendingCluster, setPendingCluster] = useState<{drafts:PreFracStageDraft[];meta:DeploymentMeta}|null>(null);
  const [fineTuneSource, setFineTuneSource] = useState<Design|null>(null);
  const [analogOpen, setAnalogOpen] = useState(false);
  const [analogLoading, setAnalogLoading] = useState(false);
  const [analogCandidates, setAnalogCandidates] = useState<AnalogCandidate[]>([]);
  const [selectedAnalogId, setSelectedAnalogId] = useState<number | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [cloneStageNumbers, setCloneStageNumbers] = useState<number[]>([]);
  const [formationPanelOpen, setFormationPanelOpen] = useState(true);
  const [builderStep, setBuilderStep] = useState<'stage'|'cluster'>('stage');
  const [saveDialogOpen,setSaveDialogOpen]=useState(false),[exportAfterSave,setExportAfterSave]=useState(false),[exportStageNumbers,setExportStageNumbers]=useState<number[]>([]);
  const clusterPanelRef=useRef<ClusterDesignPanelHandle>(null);
  const [formationInputs, setFormationInputs] = useState<FormationInputs | null>(c0?.formationInputs ?? null);
  const [scoringConfig] = useState<ScoringConfig>(() => loadScoringConfig());
  const [stageOverrides, setStageOverrides] = useState<Record<string, ScoringConfig>>(() => loadStageOverrides());
  const visibleDesigns = useMemo(() => deduplicateFractureDesigns(designs), [designs]);

  const updateStageOverrides = (next: Record<string, ScoringConfig>) => {
    setStageOverrides(next);
    localStorage.setItem(STAGE_SCORING_OVERRIDES_KEY, JSON.stringify(next));
  };

  useEffect(() => {
    setCloneStageNumbers([]);
  }, [selId, selStage]);

  useEffect(() => {
    let cancelled = false;
    setIndexedEvaluationResult(undefined);
    if (!projectId) return () => { cancelled = true; };
    loadActiveEvaluationRun(projectId).then((run) => {
      if (!cancelled) setIndexedEvaluationResult(run ? adaptEvaluationRunToLegacyResult(run) ?? undefined : undefined);
    }).catch(() => {
      // IndexedDB不可用时保留旧localStorage只读兼容结果；甜点评价页会显示明确存储错误。
    });
    return () => { cancelled = true; };
  }, [projectId]);

  const fetchAll = async () => {
    if (!projectId) { setLoading(false); return; }
    const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);
    const [l, s, e, li, d, fi] = await Promise.all([
      formationAPI.getLayers(projectId),
      wellboreAPI.getSurvey(projectId),
      sweetSpotAPI.list(projectId),
      loggingAPI.getInterpretation(projectId).catch(() => ({ data: [] })),
      fractureAPI.listDesigns(projectId),
      fractureAPI.getFormationInputs(projectId, { include_samples: false }).catch(() => ({ data: null })),
    ]);
    const L: Layer[] = un(l.data).map((r: any) => {
      const lith = LITH_ZH[r.lithology ?? r.lith] ?? '其他';
      return {
        name: r.name ?? '未命名',
        top: Number(r.top_depth ?? 0), bottom: Number(r.bottom_depth ?? 0),
        color: r.color_code || LITH_COLOR[lith] || '#707078',
        target: Boolean(r.is_target),
      };
    });
    const S: Station[] = un(s.data).map((r: any) => ({
      md: Number(r.md ?? 0),
      inc: Number(r.inclination ?? 0),
      azi: Number(r.azimuth ?? 0),
      tvd: Number(r.tvd ?? 0),
      ns: r.ns == null ? null : Number(r.ns),
      ew: r.ew == null ? null : Number(r.ew),
    })).sort((x: Station, y: Station) => x.md - y.md);
    const E: Eval[] = un(e.data).map((r: any) => ({
      top: Number(r.top_depth ?? 0), bottom: Number(r.bottom_depth ?? 0),
      grade: String(r.grade ?? 'III'),
      score: Number(r.composite_score ?? 0.5),
    })).sort((x: Eval, y: Eval) => x.top - y.top);
    const LI: LogInterp[] = un(li.data).map((r: any) => ({
      top_depth: Number(r.top_depth ?? 0),
      bottom_depth: Number(r.bottom_depth ?? 0),
      lithology: String(r.lithology ?? ''),
      porosity: r.porosity == null ? null : Number(r.porosity),
      permeability: r.permeability == null ? null : Number(r.permeability),
      water_saturation: r.water_saturation == null ? null : Number(r.water_saturation),
      oil_saturation: r.oil_saturation == null ? null : Number(r.oil_saturation),
      fluid_type: String(r.fluid_type ?? ''),
    })).sort((x: LogInterp, y: LogInterp) => x.top_depth - y.top_depth);
    const ds: Design[] = un(d.data);
    const FI = (fi.data as FormationInputs | null) || null;
    setLayers(L); setSurvey(S); setEvals(E); setLogInterps(LI); setDesigns(ds);
    setFormationInputs(FI);
    if (projectId) FP_CACHE[projectId] = { layers: L, survey: S, evals: E, logInterps: LI, designs: ds, formationInputs: FI };
    const active = ds.find((x) => x.is_active) ?? ds[0];
    setSelId(active ? active.id : null);
    if (projectId && active) setEngineeringDesignContext(projectId, active.id);
  };

  useEffect(() => {
    if (projectId && !FP_CACHE[projectId]) setLoading(true);
    fetchAll().catch((err) => message.error(err?.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const mdToTvd = useMemo(() => {
    const arr = survey.map((s) => ({ x: s.md, y: s.tvd }));
    return (md: number) => interp(arr, md);
  }, [survey]);

  const lateral = useMemo<[number, number] | null>(() => {
    if (!survey.length) return null;
    const st = survey.find((s) => s.inc >= 85);
    if (!st) return null;
    return [st.md, survey[survey.length - 1].md];
  }, [survey]);

  const quickDeployEvaluation = useMemo<SweetSpotEvaluationResult | undefined>(() => {
    if (evaluationResult) return evaluationResult;
    if (!evals.length) return undefined;
    const top = Math.max(lateral?.[0] ?? evals[0].top, evals[0].top);
    const bottom = Math.min(lateral?.[1] ?? evals[evals.length - 1].bottom, evals[evals.length - 1].bottom);
    const rows = evals.filter((item) => item.bottom > top && item.top < bottom);
    if (!rows.length || bottom <= top) return undefined;
    const scoreOf = (value: number) => value > 1 ? value / 100 : value;
    const curve = rows.flatMap((item) => [
      {depth: Math.max(top, item.top), value: scoreOf(item.score)},
      {depth: Math.min(bottom, item.bottom), value: scoreOf(item.score)},
    ]).sort((a, b) => a.depth - b.depth);
    return {
      wellId: currentProject?.well_name || `project-${projectId}`,
      intervalTop: top,
      intervalBottom: bottom,
      depthUnit: 'm',
      reservoirQualityCurve: curve,
      completionQualityCurve: curve,
      compositeSweetnessCurve: curve,
      highQualityIntervals: rows.filter((item) => item.grade === 'I' || item.grade === 'II').map((item) => ({top:item.top,bottom:item.bottom,avgScore:scoreOf(item.score)})),
      poorQualityIntervals: rows.filter((item) => item.grade === 'IV' || item.grade === 'V').map((item) => ({top:item.top,bottom:item.bottom,avgScore:scoreOf(item.score)})),
      heterogeneityBreakpoints: rows.slice(1).filter((item,index) => item.grade !== rows[index].grade).map((item) => ({depth:item.top,reason:'综合品质等级变化'})),
      recommendedStageBoundaries: rows.map((item) => ({top:Math.max(top,item.top),bottom:Math.min(bottom,item.bottom),reason:`综合品质 ${item.grade} 类区间`})),
      recommendedClusterCandidates: [],
      sourceCurveIds: ['backend:sweetspot.composite_score'],
      evaluationConfig: {source:'project_database_compatibility'},
      generatedAt: new Date().toISOString(),
    };
  }, [evaluationResult, evals, lateral, currentProject?.well_name, projectId]);
  const formationBoundaries = useMemo(() => {
    const range = lateral;
    const interpretedRows = logInterps.flatMap((item, index) => {
      const previous = logInterps[index - 1];
      const points:Array<{depth:number;reason:string}>=[];
      if (!previous || previous.lithology !== item.lithology) points.push({depth:item.top_depth,reason:`岩性界面：${previous?.lithology||'井段起点'} → ${item.lithology||'未解释'}`});
      if (index === logInterps.length - 1) points.push({depth:item.bottom_depth,reason:`岩性解释区间底界：${item.lithology||'未解释'}`});
      return points;
    });
    const layerRows = layers.flatMap((item, index) => {
      const previous = layers[index - 1];
      const points:Array<{depth:number;reason:string}>=[];
      points.push({depth:item.top,reason:`地层界面：${previous?.name||'井段起点'} → ${item.name||'未命名层'}`});
      if (index === layers.length - 1) points.push({depth:item.bottom,reason:`地层解释区间底界：${item.name||'未命名层'}`});
      return points;
    });
    const rows = [...interpretedRows,...layerRows]
      .filter(item=>Number.isFinite(item.depth)&&(!range||(item.depth>=range[0]&&item.depth<=range[1])))
      .sort((a,b)=>a.depth-b.depth);
    return rows.filter((item,index)=>rows.findIndex(other=>Math.abs(other.depth-item.depth)<.01)===index);
  },[logInterps,layers,lateral]);

  const design = designs.find((d) => d.id === selId) ?? null;
  const selectEngineeringDesign = async (nextId: number) => {
    const previousActive = designs.find((item) => item.is_active);
    setSelId(nextId);
    setSelStage(null);
    if (!projectId || previousActive?.id === nextId) {
      if (projectId) setEngineeringDesignContext(projectId, nextId);
      return;
    }
    try {
      await fractureAPI.activateDesign(nextId);
      setEngineeringDesignContext(projectId, nextId, previousActive?.id);
      setDesigns((current) => current.map((item) => ({ ...item, is_active: item.id === nextId })));
    } catch (error: any) {
      setSelId(previousActive?.id ?? null);
      message.error(error?.response?.data?.detail || '当前工程方案切换失败');
    }
  };
  useEffect(() => {
    if (!design?.notes?.startsWith('[DRAFT]') || draftManualOpenedRef.current === design.id) return;
    draftManualOpenedRef.current = design.id;
    setFineTuneSource(null);
    setImportOpen(true);
  }, [design?.id, design?.notes]);
  const comparedDesigns = visibleDesigns.filter((item) => compareIds.includes(item.id));
  const getOptimizationAdvice = (item: Design) => {
    const result = scoreFracPlan(item, evals, effectiveScoringConfig);
    const counts = new Map<string, number>();
    result.checks.forEach((check) => {
      check.suggestions.forEach((suggestion) => {
        counts.set(suggestion, (counts.get(suggestion) || 0) + 1);
      });
    });
    const suggestions = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([suggestion]) => suggestion);
    return suggestions.length ? suggestions.join('；') : result.summary.join('；');
  };
  const displayedLateral = design?.design_data?.lateral || lateral;
  const stages = useMemo(
    () => (design?.stages ?? []).slice().sort((a, b) => a.stage_number - b.stage_number),
    [design]
  );
  useEngineeringAIContext(projectId, { scope: 'builder', design_id: design?.id, name: design?.name, stages, stage_cluster_design: design?.design_data?.stage_cluster_design ?? [] });
  const stageClusterMap = useMemo(() => {
    const rows = design?.design_data?.stage_cluster_design || [];
    return new Map(rows.map((row) => [row.stage_number, row]));
  }, [design]);
  const currentClusterDrafts = useMemo<PreFracStageDraft[]>(() => stages.map((stage) => {
    const row=stageClusterMap.get(stage.stage_number),positions=row?.cluster_positions_md??row?.recommended_process?.cluster_positions_md??[];
    const spacing=Number(row?.cluster_spacing??design?.cluster_spacing??12),clusterLength=1;
    return {stageNumber:stage.stage_number,top:stage.top_perf,bottom:stage.bottom_perf,length:Number((stage.bottom_perf-stage.top_perf).toFixed(2)),avgScore:row?.quality_score??null,reason:'当前方案已保存分段结果',settings:{clusterCount:positions.length||row?.cluster_per_stage||design?.cluster_per_stage||6,clusterSpacing:spacing,clusterLength},clusters:positions.map((depth,index)=>({id:`saved-${stage.stage_number}-${index+1}`,stageNumber:stage.stage_number,depth,top:depth-clusterLength/2,bottom:depth+clusterLength/2,score:null,source:'geometric' as const,reason:'当前方案已保存簇位'})),clusterWarning:null};
  }),[stages,stageClusterMap,design]);
  const inferredReservoirType = useMemo(
    () => formationInputs?.reservoir_type || inferReservoirFromLogs(logInterps, layers),
    [formationInputs, logInterps, layers]
  );
  const effectiveScoringConfig = useMemo<ScoringConfig>(
    () => ({ ...scoringConfig, reservoirType: inferredReservoirType }),
    [scoringConfig, inferredReservoirType]
  );
  const stageKey = (stageNumber: number) => `${design?.id ?? 'none'}:${stageNumber}`;
  const stageConfigByNumber = useMemo(() => {
    if (!design) return {};
    return Object.fromEntries(stages.map((s) => {
      const override = stageOverrides[`${design.id}:${s.stage_number}`];
      return [s.stage_number, override ? { ...override, reservoirType: inferredReservoirType } : undefined];
    }));
  }, [design, stages, stageOverrides, inferredReservoirType]);
  const stageChecks = useMemo(() => {
    if (!design) return new Map<number, StageCheck>();
    return new Map(stages.map((s) => {
      const stageCluster = stageClusterMap.get(s.stage_number);
      const effectiveDesign = stageCluster
        ? { ...design, cluster_per_stage: stageCluster.cluster_per_stage, cluster_spacing: stageCluster.cluster_spacing }
        : design;
      return [s.stage_number, scoreStageCluster(s, effectiveDesign, evals, stageConfigByNumber[s.stage_number] || effectiveScoringConfig)];
    }));
  }, [design, stages, evals, effectiveScoringConfig, stageConfigByNumber, stageClusterMap]);
  const planScore = useMemo(() => design ? scoreFracPlan({ ...design, stages }, evals, effectiveScoringConfig, stageConfigByNumber) : null, [design, stages, evals, effectiveScoringConfig, stageConfigByNumber]);
  const activeTemplate = useMemo(() => buildScoringTemplate(effectiveScoringConfig), [effectiveScoringConfig]);

  const gradeAt = (md: number) => {
    const e = evals.find((x) => x.top <= md && md < x.bottom);
    return e ? e.grade : 'III';
  };

  const handleAuto = async (
    mode: 'legacy' | 'data_driven' = 'legacy',
    relaxed = false,
  ) => {
    if (!projectId) return;
    setBusy(true);
    try {
      const { data } = await fractureAPI.autoDesign({
        project_id: projectId,
        options: {
          mode,
          reservoir_type: activeTemplate.reservoirType,
          design_intent: activeTemplate.intent,
          log_interpretation_source: 'logging/interpretation',
          ...(mode === 'data_driven' && relaxed ? {
            max_stress_range_mpa: 6,
            min_effective_reservoir_ratio: 0.2,
            max_missing_ratio: 0.65,
            length_weight: 0.14,
          } : {}),
          ...(scoringConfig.custom ? { scoring_template: {
            stage_length: activeTemplate.stageLength,
            clusters: activeTemplate.clusters,
            cluster_spacing: activeTemplate.clusterSpacing,
            fluid_intensity: activeTemplate.fluidIntensity,
            proppant_intensity: activeTemplate.proppantIntensity,
            max_cluster_coverage_ratio: activeTemplate.maxClusterCoverageRatio,
          } } : {}),
        },
      });
      await fetchAll();
      setSelId(data.id);
      const fieldConstrained = data.design_data?.mode === 'field_constrained_data_driven';
      if (data.generation_status === 'existing') {
        message.info('该方案已存在，已切换到已有方案');
      } else {
        message.success(fieldConstrained
          ? '已生成类比井约束优化方案'
          : `已生成${mode === 'data_driven' ? '地层数据驱动' : '甜点规则'}方案: ${data.stage_count} 段, 总液量 ${data.total_fluid} m³`);
      }
      askDesignAI('请解释刚生成的智能压裂方案采用了哪些数据库案例和地层约束，并指出段簇参数与待复核风险。', undefined, data.id);
    } catch (err: any) {
      const d = err?.response?.data;
      if (mode === 'data_driven' && d?.status === 'infeasible') {
        const failed = Array.isArray(d.failed_constraints)
          ? d.failed_constraints.map((key: string) => CONSTRAINT_LABELS[key] || key)
          : [];
        const suggestions = Array.isArray(d.suggestions) ? d.suggestions : [];
        Modal.confirm({
          title: relaxed ? '放宽后仍无法完成数据驱动分段' : '当前约束下无法生成新方案',
          width: 560,
          okText: relaxed ? '我知道了' : '适度放宽约束后重试',
          cancelText: relaxed ? undefined : '保留当前方案',
          content: (
            <div style={{ fontSize: 12, color: TH.textSec, lineHeight: 1.7 }}>
              <div>
                算法已经执行，但没有找到覆盖完整水平段的可行分段，因此没有创建新方案，
                当前页面仍显示旧方案。
              </div>
              {d.infeasible_md !== undefined && (
                <div><b>不可行位置：</b>MD {d.infeasible_md} m</div>
              )}
              {!!failed.length && (
                <div><b>涉及约束：</b>{failed.join(' / ')}</div>
              )}
              {!!suggestions.length && (
                <div><b>调整建议：</b>{suggestions.join('；')}</div>
              )}
              {!relaxed && (
                <div style={{ color: TH.textMuted, marginTop: 4 }}>
                  重试仅适度放宽应力差、有效储层比例和缺失率，不会回退到旧自动设计。
                </div>
              )}
            </div>
          ),
          onOk: relaxed ? undefined : () => handleAuto('data_driven', true),
        });
      } else {
        const detail = typeof d === 'string'
          ? d
          : d?.detail || err?.message || '自动设计失败';
        Modal.error({
          title: '自动设计请求失败',
          content: (
            <div style={{ fontSize: 12, lineHeight: 1.7 }}>
              <div>{detail}</div>
              <div style={{ color: TH.textMuted, marginTop: 4 }}>
                当前方案没有被修改。请检查登录状态、后端服务和输入数据。
              </div>
            </div>
          ),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const prepareClusterDesign = async (drafts:PreFracStageDraft[],meta:DeploymentMeta) => {
    setPendingCluster({drafts,meta});setQuickDeployOpen(false);setSmartDeployOpen(false);setImportOpen(false);setBuilderStep('cluster');
  };
  const openEngineeringSmartDesign = () => {
    if (!design) {
      message.warning('请先选择或新建一个工程方案');
      return;
    }
    if (!quickDeployEvaluation) {
      message.warning('当前项目缺少甜点评价或地层品质数据，暂不能进行智能分段');
      return;
    }
    setSmartDeployOpen(true);
  };
  const startFineTune = () => {
    if (!design || !stages.length) {
      message.warning('请先选择一个已有分段方案');
      return;
    }
    setFineTuneSource(design);
    setImportOpen(true);
  };
  const deployQuickDesign = async (drafts: PreFracStageDraft[], meta: DeploymentMeta) => {
    if (!projectId || !drafts.length) return;
    const currentLength = stages.reduce((sum, row) => sum + Math.max(0, row.bottom_perf - row.top_perf), 0);
    const fluidIntensity = currentLength > 0 && design?.total_fluid
      ? design.total_fluid / currentLength
      : (activeTemplate.fluidIntensity.min + activeTemplate.fluidIntensity.max) / 2;
    const proppantIntensity = currentLength > 0 && design?.total_proppant
      ? design.total_proppant / currentLength
      : (activeTemplate.proppantIntensity.min + activeTemplate.proppantIntensity.max) / 2;
    const pumpRate = design?.pump_rate || 14;
    const stageRows = drafts.map((row) => ({
      stage_number: row.stageNumber,
      top_perf: row.top,
      bottom_perf: row.bottom,
      fluid_volume: Number((row.length * fluidIntensity).toFixed(1)),
      proppant_mass: Number((row.length * proppantIntensity).toFixed(1)),
      schedule: [],
    }));
    const totalFluid = stageRows.reduce((sum, row) => sum + row.fluid_volume, 0);
    const totalProppant = stageRows.reduce((sum, row) => sum + row.proppant_mass, 0);
    const clusterTotal = drafts.reduce((sum, row) => sum + row.clusters.length, 0);
    const spacingRows = drafts.flatMap((row) => row.clusters.slice(1).map((cluster, index) => cluster.depth - row.clusters[index].depth));
    const stageClusterDesign = drafts.map((row) => ({
      stage_number: row.stageNumber,
      cluster_per_stage: row.clusters.length,
      cluster_spacing: row.clusters.length > 1
        ? Number(((row.clusters[row.clusters.length - 1].depth - row.clusters[0].depth) / (row.clusters.length - 1)).toFixed(1))
        : 0,
      cluster_positions_md: row.clusters.map((cluster) => cluster.depth),
      quality_score: row.avgScore,
      reason: row.reason,
      recommended_process: {
        stage_length_m: row.length,
        cluster_count: row.clusters.length,
        cluster_positions_md: row.clusters.map((cluster) => cluster.depth),
        fluid_volume_m3: Number((row.length * fluidIntensity).toFixed(1)),
        proppant_mass_t: Number((row.length * proppantIntensity).toFixed(1)),
        pump_rate_m3min: pumpRate,
        perforation_layout: meta.mode === 'engineering' ? 'quality_constrained' : 'equal_spaced',
        note: row.reason,
      },
    }));
    const draftSource = design?.notes?.startsWith('[DRAFT]') ? design : null;
    const existingNames = visibleDesigns.map(row => row.name);
    const generatedName = draftSource
      ? cleanDesignName(draftSource.name)
      : meta.sourceDesignName
      ? uniqueDesignName(`${cleanDesignName(meta.sourceDesignName)}微调方案`, existingNames)
      : uniqueDesignName(generatedDesignName(currentProject?.well_name || '当前井', meta.mode === 'engineering' ? '甜点分段' : meta.mode === 'geometric' ? '几何分段' : '自定义分段'), existingNames);
    setBusy(true);
    try {
      const {data} = await fractureAPI.importPlan({
        project_id: projectId,
        design: {
          name: generatedName,
          stage_count: drafts.length,
          cluster_per_stage: Math.max(1, Math.round(clusterTotal / drafts.length)),
          cluster_spacing: spacingRows.length ? Number((spacingRows.reduce((sum, value) => sum + value, 0) / spacingRows.length).toFixed(1)) : 0,
          stage_spacing: Number((drafts.reduce((sum, row) => sum + row.length, 0) / drafts.length).toFixed(1)),
          pump_rate: pumpRate,
          total_fluid: Number(totalFluid.toFixed(1)),
          total_proppant: Number(totalProppant.toFixed(1)),
          fluid_type: design?.fluid_type || '滑溜水',
          proppant_type: design?.proppant_type || '石英砂',
          max_proppant_conc: design?.max_proppant_conc || 220,
          is_active: true,
          notes: meta.sourceDesignName
            ? `基于“${meta.sourceDesignName}”微调生成；原方案保留。`
            : `${meta.mode === 'engineering' ? '综合品质相似性约束' : meta.mode === 'geometric' ? '几何等长' : '人工分层线'}分段设计；段界已锁定，原方案保留。`,
          design_data: {
            mode: `quick_deploy_${meta.mode}`,
            lateral: [meta.intervalTop, meta.intervalBottom],
            stage_boundaries_locked: true,
            quick_deploy_parameters: meta,
            stage_cluster_design: stageClusterDesign,
            ...(meta.sourceDesignId ? {revision_context:{source_design_id:meta.sourceDesignId,source_design_name:meta.sourceDesignName,status:'new_revision',generated_at:new Date().toISOString()}} : {}),
          },
        },
        stages: stageRows,
      });
      if (draftSource && data.id !== draftSource.id) await fractureAPI.bulkDeleteDesigns([draftSource.id]);
      await fetchAll();
      setSelId(data.id);
      setQuickDeployOpen(false);
      setImportOpen(false);
      setPendingCluster(null);
      setFineTuneSource(null);
      if (meta.mode === 'engineering') askDesignAI('请分析刚完成的分段分簇方案，解释段界、簇位与甜点地层的关系及风险。', undefined, data.id);
      message.success(draftSource ? `“${draftSource.name}”已完成分段设计` : meta.sourceDesignName ? `已生成“${meta.sourceDesignName}”的微调新方案，原方案已保留` : `已部署 ${drafts.length} 段，原方案已保留`);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '分段设置失败，当前方案未修改');
    } finally {
      setBusy(false);
    }
  };

  const openAnalogDesign = async () => {
    if (!projectId) return;
    setAnalogOpen(true);
    setAnalogLoading(true);
    setSelectedAnalogId(null);
    try {
      const { data } = await fractureAPI.listAnalogCandidates(projectId);
      setAnalogCandidates(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setAnalogCandidates([]);
      message.error(err?.response?.data?.detail || '邻井择选方案读取失败');
    } finally {
      setAnalogLoading(false);
    }
  };

  const runAnalogDesign = async () => {
    if (!projectId || !selectedAnalogId) return;
    setBusy(true);
    try {
      const selectedCandidate = analogCandidates.find(item => item.design_id === selectedAnalogId);
      const { data } = await fractureAPI.createAnalogDesign({ project_id: projectId, source_design_id: selectedAnalogId, allow_unvalidated: !selectedCandidate?.verified });
      await fetchAll();
      setSelId(data.id);
      setSelStage(null);
      setAnalogOpen(false);
      if (data.generation_status === 'existing') {
        message.info('该方案已存在，已切换到已有方案');
      } else {
        message.success(`邻井择选方案已生成：${data.stage_count} 段`);
      }
      askDesignAI('请解释刚生成的邻井择选方案：为什么选择该邻井、参数如何矢量转换、哪些风险需要复核。', selectedAnalogId, data.id);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '邻井择选方案生成失败');
    } finally {
      setBusy(false);
    }
  };

  const askDesignAI = (prompt = '请解释当前智能压裂设计的数据库依据、类比井约束、段簇参数和需要人工复核的风险。', neighborDesignId?: number, designId = design?.id) => {
    window.dispatchEvent(new CustomEvent('fracsight-ai-open', {
      detail: { prompt, designId, neighborDesignId, autoSend: true, projectId },
    }));
  };

  const openComparison = () => {
    setCompareIds(selId ? [selId] : []);
    setCompareOpen(true);
  };

  const toggleComparison = (id: number, checked: boolean) => {
    if (checked && !compareIds.includes(id)) {
      setCompareIds([...compareIds, id]);
      return;
    }
    if (!checked) setCompareIds(compareIds.filter((value) => value !== id));
  };

  const handleClearStageCluster = async () => {
    if (!design) return;
    Modal.confirm({
      title: `清空「${design.name}」的分段分簇方案?`,
      content: '方案名称和基础参数将保留，现有分段、分簇及其泵注数据将被清空。',
      okText: '确认清空', cancelText: '取消', okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        try {
          await fractureAPI.clearStageCluster(design.id);
          setPendingCluster(null);
          setSelStage(null);
          setBuilderStep('stage');
          await fetchAll();
          setSelId(design.id);
          message.success('分段分簇方案已清空，当前方案已保留');
        } catch (error: any) {
          message.error(error?.response?.data?.detail || '清空分段分簇方案失败');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const downloadPlan = (selectedStageNumbers = stages.map((stage) => stage.stage_number)) => {
    if (!design) return;
    const selectedStages = stages.filter((stage) => selectedStageNumbers.includes(stage.stage_number));
    if (!selectedStages.length) return void message.warning('请至少选择一个需要导出的压裂段');
    const overview = [
      ['方案名称', design.name], ['方案总段数', stages.length], ['本次导出段数', selectedStages.length], ['设计簇数/段', design.cluster_per_stage],
      ['设计簇间距(m)', design.cluster_spacing], ['设计排量(m³/min)', design.pump_rate],
      ['总液量(m³)', design.total_fluid], ['总支撑剂量(t)', design.total_proppant],
      ['压裂液类型', design.fluid_type], ['支撑剂类型', design.proppant_type],
      ['最高支撑剂浓度(kg/m³)', design.max_proppant_conc], ['方案状态', design.is_active ? '当前方案' : '备选方案'],
    ];
    const stageRows = selectedStages.map((stage) => {
      const cluster = stageClusterMap.get(stage.stage_number), check = stageChecks.get(stage.stage_number);
      const positions = cluster?.cluster_positions_md ?? cluster?.recommended_process?.cluster_positions_md ?? [];
      const spacing = cluster?.cluster_spacing_summary?.average_m ?? cluster?.cluster_spacing ?? design.cluster_spacing;
      return {
        段号: stage.stage_number, '段顶测深(m)': stage.top_perf, '段底测深(m)': stage.bottom_perf,
        '段长(m)': Number((stage.bottom_perf - stage.top_perf).toFixed(2)),
        簇数: (cluster?.cluster_per_stage ?? positions.length) || design.cluster_per_stage,
        '平均簇间距(m)': spacing, '簇位置MD(m)': positions.join('、'),
        '压裂液量(m³)': stage.fluid_volume, '支撑剂量(t)': stage.proppant_mass,
        '综合评分': check?.score ?? '', '评分状态': check?.label ?? '',
      };
    });
    const scheduleRows = selectedStages.flatMap((stage) => (stage.schedule ?? []).map((step, index) => ({
      段号: stage.stage_number, 级次: index + 1, 施工阶段: step.step_type ?? step.step ?? '',
      '排量(m³/min)': step.rate, '压裂液体积(m³)': step.volume ?? step.fluid_m3 ?? '',
      '支撑剂浓度(kg/m³)': step.sand_concentration ?? step.sand_conc ?? '',
      '阶段支撑剂量(t)': step.sand_amount ?? step.proppant_t ?? '',
    })));
    const workbook = XLSX.utils.book_new(), overviewSheet = XLSX.utils.aoa_to_sheet(overview), stageSheet = XLSX.utils.json_to_sheet(stageRows);
    overviewSheet['!cols'] = [{ wch: 25 }, { wch: 34 }];
    stageSheet['!cols'] = [{ wch: 8 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 9 }, { wch: 17 }, { wch: 46 }, { wch: 17 }, { wch: 17 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(workbook, overviewSheet, '方案概览');
    XLSX.utils.book_append_sheet(workbook, stageSheet, '分段分簇');
    if (scheduleRows.length) {
      const scheduleSheet = XLSX.utils.json_to_sheet(scheduleRows);
      scheduleSheet['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 24 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(workbook, scheduleSheet, '泵注程序');
    }
    XLSX.writeFile(workbook, `压裂方案_${design.name.replace(/[\\/:*?"<>|]/g, '_')}.xlsx`);
    message.success('压裂方案已导出为 Excel 文件');
  };
  const saveStageNumbers=builderStep==='cluster'?(pendingCluster?.drafts??currentClusterDrafts).map(row=>row.stageNumber):stages.map(row=>row.stage_number);
  const openSaveDialog=()=>{if(!design)return;setExportAfterSave(false);setExportStageNumbers(saveStageNumbers);setSaveDialogOpen(true);};
  const confirmSavePlan=async()=>{if(exportAfterSave&&!exportStageNumbers.length)return void message.warning('请至少选择一个需要导出的压裂段');if(builderStep==='cluster'){if(!clusterPanelRef.current)return void message.warning('分簇编辑器尚未准备完成');await clusterPanelRef.current.confirm();}else{message.success('当前分段方案已保存');}if(exportAfterSave)downloadPlan(exportStageNumbers);setSaveDialogOpen(false);};

  // 爱压 AI 操作模式: 两条命令都只打开本页已有的对话框, 最终由用户在对话框里确认
  useAgentCommands({
    'staging.smart_design': () => {
      if (!design) throw new Error('请先选择或新建一个工程方案');
      if (!quickDeployEvaluation) throw new Error('当前项目缺少甜点评价或地层品质数据, 暂不能智能分段');
      openEngineeringSmartDesign();
      return '已打开智能分段对话框, 请核对后确认部署';
    },
    'staging.save': () => {
      if (!design) throw new Error('当前没有选中的方案');
      openSaveDialog();
      return '已打开保存对话框, 请确认保存';
    },
  }, () => (design
    ? `分段分簇: 方案 #${design.id} ${design.name}, ${stages.length} 段${busy ? ', 正在处理' : ''}`
    : '分段分簇: 未选择方案'));

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;
  // 不再用"加载数据中"全屏阻塞 - 顶栏立刻渲染, body 等数据到位再显示

  const curStage = stages.find((s) => s.stage_number === selStage) ?? null;
  const curCheck = curStage ? stageChecks.get(curStage.stage_number) : null;
  const curStageCluster = curStage ? stageClusterMap.get(curStage.stage_number) : undefined;
  const curProcess = curStageCluster?.recommended_process;
  const curStageKey = curStage ? stageKey(curStage.stage_number) : null;
  const curStageConfig = curStageKey ? stageOverrides[curStageKey] : undefined;
  const curStageTemplate = buildScoringTemplate(curStageConfig ? { ...curStageConfig, reservoirType: inferredReservoirType } : effectiveScoringConfig);
  const avgCheckScore = planScore?.score ?? null;
  const btn = (bg: string, fg: string, border?: string): React.CSSProperties => ({
    padding: '5px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600,
    cursor: busy ? 'wait' : 'pointer', border: border || 'none', background: bg, color: fg,
    whiteSpace: 'nowrap',
  });
  const enableStageOverride = (stageNumber: number) => {
    const key = stageKey(stageNumber);
    const targetCheck = stageChecks.get(stageNumber);
    const targetCluster = stageClusterMap.get(stageNumber);
    const baseConfig = { reservoirType: inferredReservoirType, intent: scoringConfig.intent };
    const baseTemplate = buildScoringTemplate(baseConfig);
    const clusterCount = targetCluster?.cluster_per_stage ?? design?.cluster_per_stage ?? Math.round((baseTemplate.clusters.min + baseTemplate.clusters.max) / 2);
    const clusterSpacing = targetCluster?.cluster_spacing_summary?.average_m ?? targetCluster?.cluster_spacing ?? design?.cluster_spacing ?? (baseTemplate.clusterSpacing.min + baseTemplate.clusterSpacing.max) / 2;
    const fluidIntensity = targetCheck?.fluidIntensity ?? (baseTemplate.fluidIntensity.min + baseTemplate.fluidIntensity.max) / 2;
    const proppantIntensity = targetCheck?.proppantIntensity ?? (baseTemplate.proppantIntensity.min + baseTemplate.proppantIntensity.max) / 2;
    updateStageOverrides({
      ...stageOverrides,
      [key]: {
        ...baseConfig,
        custom: {
          stageLength: { ...baseTemplate.stageLength },
          clusters: { min: Math.round(clusterCount), max: Math.round(clusterCount) },
          clusterSpacing: { min: Number(clusterSpacing.toFixed(1)), max: Number(clusterSpacing.toFixed(1)) },
          fluidIntensity: { min: Number(fluidIntensity.toFixed(1)), max: Number(fluidIntensity.toFixed(1)) },
          proppantIntensity: { min: Number(proppantIntensity.toFixed(2)), max: Number(proppantIntensity.toFixed(2)) },
          maxClusterCoverageRatio: baseTemplate.maxClusterCoverageRatio,
        },
      },
    });
  };
  const removeStageOverride = (stageNumber: number) => {
    const key = stageKey(stageNumber);
    const next = { ...stageOverrides };
    delete next[key];
    updateStageOverrides(next);
  };
  const updateStageConfig = (stageNumber: number, nextConfig: ScoringConfig) => {
    updateStageOverrides({
      ...stageOverrides,
      [stageKey(stageNumber)]: nextConfig,
    });
  };
  const setStageFixedValue = (
    stageNumber: number,
    key: 'clusters' | 'clusterSpacing' | 'fluidIntensity' | 'proppantIntensity',
    value: number
  ) => {
    const currentConfig = { ...(stageOverrides[stageKey(stageNumber)] || effectiveScoringConfig), reservoirType: inferredReservoirType };
    updateStageConfig(stageNumber, {
      ...currentConfig,
      custom: {
        ...currentConfig.custom,
        [key]: { min: value, max: value },
      },
    });
  };
  const cloneCurrentStageDesign = () => {
    if (!curStageConfig || !cloneStageNumbers.length) return;
    const next = { ...stageOverrides };
    cloneStageNumbers.forEach((stageNumber) => {
      next[stageKey(stageNumber)] = JSON.parse(JSON.stringify(curStageConfig)) as ScoringConfig;
    });
    updateStageOverrides(next);
    message.success(`已将第 ${curStage?.stage_number} 段设计参数克隆至 ${cloneStageNumbers.length} 个目标段`);
    setCloneStageNumbers([]);
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#F4F6F8', fontFamily: 'inherit', color: TH.text }}>
      {/* Header */}
      <div style={{ minHeight: 116, padding: '13px 22px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', columnGap: 16, rowGap: 10, background: '#FFFFFF', borderBottom: `1px solid ${TH.headerBorder}` }}>
        <div style={{ minWidth: 0, gridColumn: 1, gridRow: 1 }}>
          <h2 style={{ fontSize: 21, lineHeight: 1.25, fontWeight: 700, color: '#1F2D3D', margin: 0, whiteSpace: 'nowrap' }}>分段分簇</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, gridColumn: '1 / -1', gridRow: 2 }}>
          <div style={{display:'flex',alignItems:'center',height:38,border:'1px solid #C9D2DC',borderRadius:5,overflow:'hidden',background:'#fff',flexShrink:0}}>
            <button type="button" onClick={()=>setBuilderStep('stage')} style={{height:'100%',minWidth:108,padding:'0 24px',border:'none',borderRight:'1px solid #C9D2DC',fontSize:13,fontWeight:builderStep==='stage'?700:500,color:builderStep==='stage'?'#fff':'#425466',background:builderStep==='stage'?'#1677FF':'#fff',cursor:'pointer'}}>① 分段</button>
            <button type="button" onClick={()=>setBuilderStep('cluster')} style={{height:'100%',minWidth:108,padding:'0 24px',border:'none',fontSize:13,fontWeight:builderStep==='cluster'?700:500,color:builderStep==='cluster'?'#fff':'#425466',background:builderStep==='cluster'?'#1677FF':'#fff',cursor:'pointer'}}>② 分簇</button>
          </div>
          {visibleDesigns.length > 0 && (
            <select value={selId ?? ''} onChange={(e) => void selectEngineeringDesign(Number(e.target.value))}
              style={{ width: 286, minWidth: 140, maxWidth: '38vw', height: 38, fontSize: 12, padding: '0 10px', borderRadius: 5, border: '1px solid #C9D2DC', background: '#fff', color: TH.text }}>
              {visibleDesigns.map((d) => (
                <option key={d.id} value={d.id}>{cleanDesignName(d.name)}</option>
              ))}
            </select>
          )}
          {displayedLateral && (
            <span style={{ fontSize: 12, padding: '7px 11px', borderRadius: 5, fontWeight: 600, background: '#FFF1EC', color: TH.accent, whiteSpace: 'nowrap' }}>
              MD {Math.round(displayedLateral[0])}–{Math.round(displayedLateral[1])} m
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, gridColumn: 2, gridRow: 1 }}>
          {design && <button onClick={() => navigate(simulationHandoffUrl(design.id))} title="将当前已保存方案交给压裂模拟校核" style={{...btn('#fff', TH.stage, `1px solid ${TH.stage}`),height:36,padding:'0 12px'}}>进入模拟</button>}
          {design && <button onClick={openSaveDialog} title="保存当前方案" style={{...btn('#fff', TH.textSec, '1px solid #C9D2DC'),height:36,padding:'0 12px'}}><SaveOutlined /> 保存方案</button>}
          {design && <button onClick={startFineTune} disabled={busy||!stages.length} title="基于此方案微调" style={{...btn('#fff', TH.textSec, '1px solid #C9D2DC'),height:36,padding:'0 12px'}}><CopyOutlined /> 基于此方案微调</button>}
          {design && <button onClick={handleClearStageCluster} disabled={busy||!stages.length} title="清空分段分簇方案" style={{...btn('#fff', '#C0392B', '1px solid #E3B6B0'),height:36,padding:'0 12px'}}><DeleteOutlined /> 清空方案</button>}
        </div>
      </div>

      <div style={{flex:1,minHeight:0,display:'flex',flexDirection:'column',background:'#F4F6F8'}}>
        {builderStep==='stage'?(
          <>
            <div style={{margin:'12px 12px 0',padding:'11px 14px',display:'flex',alignItems:'center',gap:9,background:'#fff',border:'1px solid #D9E0E7',borderRadius:6,boxShadow:'0 1px 3px rgba(31,45,61,.04)'}}>
              <span style={{fontSize:13,fontWeight:700,color:'#35495E',marginRight:4,whiteSpace:'nowrap'}}>分段设计工具</span>
              <button onClick={()=>{setQuickDeployMode('engineering');setQuickDeployOpen(true);}} disabled={busy||!quickDeployEvaluation} style={{...btn('#fff','#2874A6','1px solid #76A4C2'),height:34,padding:'0 14px'}}><ApartmentOutlined/> 甜点分段</button>
              <button onClick={()=>{setQuickDeployMode('geometric');setQuickDeployOpen(true);}} disabled={busy} style={{...btn('#fff','#2874A6','1px solid #76A4C2'),height:34,padding:'0 14px'}}><ApartmentOutlined/> 几何分段</button>
              <button onClick={()=>{setFineTuneSource(null);setImportOpen(true);}} disabled={busy} style={{...btn('#fff','#2874A6','1px solid #76A4C2'),height:34,padding:'0 14px'}}><LineChartOutlined/> 地层曲线分段</button>
              <button onClick={openEngineeringSmartDesign} disabled={busy||!design} style={{...btn('#fff','#2874A6','1px solid #76A4C2'),height:34,padding:'0 14px'}}><DatabaseOutlined/> 智能设计</button>
            </div>
            {projectId&&<StageDesignResultPanel projectId={projectId} stages={pendingCluster?.drafts??currentClusterDrafts} evaluation={quickDeployEvaluation} onFineTune={startFineTune} fineTuneDisabled={busy||!design}/>}
          </>
        ):projectId&&(pendingCluster?.drafts.length||currentClusterDrafts.length)?(
          <div style={{padding:12,flex:1,minHeight:0,background:'#fff'}}>
            <ClusterDesignPanel ref={clusterPanelRef} key={pendingCluster?`pending-${pendingCluster.drafts.length}`:`saved-${design?.id??0}`} projectId={projectId} stages={pendingCluster?.drafts??currentClusterDrafts} evaluation={quickDeployEvaluation} busy={busy}
              onBack={()=>setBuilderStep('stage')}
              onConfirm={(rows,clusterDesign:ClusterDesignMeta)=>{const base=pendingCluster?.meta??{mode:'custom' as const,intervalTop:displayedLateral?.[0]??rows[0]?.top??0,intervalBottom:displayedLateral?.[1]??rows[rows.length-1]?.bottom??0};return deployQuickDesign(rows,{...base,clusterDesign});}}/>
          </div>
        ):(
          <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:10,color:TH.textMuted}}><div>暂无可用分段结果</div><button type="button" onClick={()=>setBuilderStep('stage')} style={btn('#fff',TH.stage,`1px solid ${TH.stage}`)}>返回分段设置</button></div>
        )}
      </div>
      <Modal open={saveDialogOpen} title="保存分段分簇方案" okText={exportAfterSave?'保存并导出':'仅保存'} cancelText="取消" confirmLoading={busy} onOk={confirmSavePlan} onCancel={()=>setSaveDialogOpen(false)} destroyOnHidden>
        <div style={{display:'grid',gap:14,padding:'4px 0'}}><Checkbox checked={exportAfterSave} onChange={event=>setExportAfterSave(event.target.checked)}><b>保存后导出 Excel</b></Checkbox><div style={{padding:12,border:`1px solid ${exportAfterSave?'#CFDCE7':'#E3E6E9'}`,borderRadius:6,background:exportAfterSave?'#F7FBFE':'#FAFAFA',opacity:exportAfterSave?1:.62}}><div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,color:'#344F68',fontSize:12}}><b>选择需要导出的压裂段</b><span><Button type="link" size="small" disabled={!exportAfterSave} onClick={()=>setExportStageNumbers(saveStageNumbers)}>全选</Button><Button type="link" size="small" disabled={!exportAfterSave} onClick={()=>setExportStageNumbers([])}>清空</Button></span></div><Checkbox.Group disabled={!exportAfterSave} value={exportStageNumbers} onChange={values=>setExportStageNumbers(values.map(Number))} options={saveStageNumbers.map(stageNumber=>({value:stageNumber,label:`第 ${stageNumber} 段`}))} style={{display:'grid',gridTemplateColumns:'repeat(5,minmax(74px,1fr))',gap:'9px 12px',width:'100%'}}/><div style={{marginTop:12,color:'#60778C',fontSize:11}}>导出内容：方案概览、所选段的分段分簇参数及已有泵注程序</div></div></div>
      </Modal>
      <Modal
        open={quickDeployOpen}
        title={quickDeployMode==='engineering'?'甜点分段':'几何分段'}
        footer={null}
        onCancel={() => setQuickDeployOpen(false)}
        destroyOnClose
        width="min(1180px, calc(100vw - 30px))"
        styles={{body:{padding:12}}}
      >
        <QuickDeployPanel
          key={`${projectId}-quick-deploy-${quickDeployMode}`}
          initialMode={quickDeployMode}
          evaluation={quickDeployEvaluation}
          fallbackInterval={lateral}
          formationBoundaries={formationBoundaries}
          deploying={busy}
          onDeploy={prepareClusterDesign}
        />
      </Modal>

      <Modal open={importOpen} title={fineTuneSource?`基于“${fineTuneSource.name}”微调并生成新方案`:'根据地层曲线划分分段'} footer={null}
        onCancel={() => {setImportOpen(false);setFineTuneSource(null);}} destroyOnClose width="min(1280px, calc(100vw - 28px))" styles={{body:{padding:'8px 12px 12px'}}}>
        {projectId?<CustomStagePanel projectId={projectId} fallbackInterval={lateral} initialStages={fineTuneSource?.stages} deploying={busy} onCalculate={(rows,meta)=>prepareClusterDesign(rows,{...meta,sourceDesignId:fineTuneSource?.id,sourceDesignName:fineTuneSource?.name})}/>:null}
      </Modal>

      <Modal
        open={smartDeployOpen}
        title={`当前方案智能设计${design ? ` · ${design.name}` : ''}`}
        footer={null}
        onCancel={() => setSmartDeployOpen(false)}
        destroyOnClose
        width="min(1180px, calc(100vw - 30px))"
        styles={{body:{padding:12}}}
      >
        <QuickDeployPanel
          key={`${projectId}-${design?.id}-engineering-smart`}
          projectId={projectId}
          designId={design?.id}
          evaluation={quickDeployEvaluation}
          fallbackInterval={displayedLateral}
          formationBoundaries={formationBoundaries}
          deploying={busy}
          smartOnly
          onDeploy={(rows, meta) => prepareClusterDesign(rows, {
            ...meta,
            sourceDesignId: design?.id,
            sourceDesignName: design?.name,
          })}
        />
      </Modal>

      <Modal
        open={analogOpen}
        title="邻井择选"
        okText="生成择选方案"
        cancelText="取消"
        okButtonProps={{ disabled: !selectedAnalogId || analogLoading, loading: busy }}
        onOk={runAnalogDesign}
        onCancel={() => setAnalogOpen(false)}
        destroyOnClose
        width={900}
      >
        <div style={{ marginBottom: 8, padding: '8px 10px', background: '#F4F7F9', border: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontSize: 12 }}>按目标层位、井型、水平段长度、储层物性、脆性、应力各向异性和综合品质构建特征向量；系统按相似度排序，生成时对段簇、液砂强度和排量进行有界转换。生成成功后将自动进入 AI 互动分析。</div>
        <div style={{ border: `1px solid ${TH.headerBorder}`, maxHeight: 245, overflow: 'auto' }}>
          {analogLoading ? (
            <div style={{ padding: 28, textAlign: 'center', color: TH.textMuted }}>读取中…</div>
          ) : analogCandidates.length ? (
            analogCandidates.map((candidate) => (
              <label key={candidate.design_id} style={{ display: 'grid', gridTemplateColumns: '28px minmax(220px,1fr) 92px 78px 72px 70px', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: `1px solid ${TH.statBorder}`, cursor: 'pointer', background: selectedAnalogId===candidate.design_id?'#EEF6F8':'#fff' }}>
                <input
                  type="radio"
                  name="analog-candidate"
                  checked={selectedAnalogId === candidate.design_id}
                  onChange={() => setSelectedAnalogId(candidate.design_id)}
                />
                <span style={{ minWidth: 0 }}>
                  <b style={{ color: TH.text }}>{candidate.well_name}</b>
                  <span style={{ color: TH.textMuted, marginLeft: 8 }}>{candidate.design_name}</span>
                </span>
                <span style={{ color: TH.textSec }}>{candidate.target_formation || '未标注层位'}</span>
                <b style={{ color: candidate.similarity_score>=80?'#177D63':candidate.similarity_score>=65?'#A46117':'#B23A32', textAlign: 'right' }}>{candidate.similarity_score.toFixed(1)} 分</b>
                <span style={{ color: TH.textSec, textAlign: 'right' }}>{candidate.distance_km==null?'距离未知':`${candidate.distance_km} km`}</span>
                <span style={{ color: TH.textSec, textAlign: 'right' }}>{candidate.stage_count} 段</span>
              </label>
            ))
          ) : (
            <div style={{ padding: 28, textAlign: 'center', color: TH.textMuted }}>暂无其他井且包含段表数据的压裂方案。请先建立邻井项目并导入实钻方案，再进行矢量择选。</div>
          )}
        </div>
        {selectedAnalogId&&(()=>{const candidate=analogCandidates.find(item=>item.design_id===selectedAnalogId);if(!candidate)return null;return <div style={{marginTop:10,border:`1px solid ${TH.headerBorder}`,background:'#fff'}}><div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',background:'#F7F8FA',borderBottom:`1px solid ${TH.statBorder}`}}><b>矢量匹配明细</b><Tag color={candidate.similarity_score>=80?'green':candidate.similarity_score>=65?'orange':'red'}>{candidate.recommendation} · {candidate.similarity_score.toFixed(1)}分</Tag><Tag color={candidate.verified?'green':'gold'}>{candidate.verified?'现场已验证':'方案待核验'}</Tag><span style={{marginLeft:'auto',color:TH.textMuted,fontSize:11}}>数据完整度 {candidate.data_completeness.toFixed(0)}%</span></div><div style={{display:'grid',gridTemplateColumns:'1.35fr .65fr',gap:10,padding:10}}><div style={{maxHeight:180,overflow:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}><thead><tr><th style={analogTh}>特征</th><th style={analogTh}>目标井</th><th style={analogTh}>邻井</th><th style={analogTh}>匹配度</th></tr></thead><tbody>{candidate.feature_components.map(row=><tr key={row.key}><td style={analogTd}>{row.label}</td><td style={analogTd}>{formatAnalogValue(row.target)}</td><td style={analogTd}>{formatAnalogValue(row.source)}</td><td style={{...analogTd,color:row.similarity==null?TH.textMuted:row.similarity>=80?'#177D63':row.similarity>=60?'#A46117':'#B23A32'}}>{row.similarity==null?'缺失':`${row.similarity.toFixed(0)}%`}</td></tr>)}</tbody></table></div><div><b style={{fontSize:11,color:TH.textSec}}>参数转换系数</b>{[['MD映射',candidate.transform.geometry_scale],['簇密度',candidate.transform.cluster_scale],['液量强度',candidate.transform.fluid_scale],['加砂强度',candidate.transform.proppant_scale],['排量',candidate.transform.rate_scale]].map(([label,value])=><div key={String(label)} style={{display:'flex',justifyContent:'space-between',padding:'7px 2px',borderBottom:`1px solid ${TH.statBorder}`,fontSize:11}}><span>{label}</span><b>× {Number(value).toFixed(3)}</b></div>)}{!candidate.verified&&<div style={{marginTop:8,color:'#9A5B13',fontSize:10,lineHeight:1.5}}>该方案尚未现场验证，生成结果将保留风险标记，须经工程师复核后使用。</div>}</div></div></div>})()}
      </Modal>

      <DraggableModal
        open={compareOpen}
        title="方案对比"
        footer={null}
        onCancel={() => setCompareOpen(false)}
        width="min(920px, calc(100vw - 32px))"
        styles={{ body: { height: 'min(390px, calc(100vh - 150px))', overflow: 'hidden', padding: 14 } }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '210px minmax(0, 1fr)', gap: 12, height: '100%', minHeight: 0 }}>
          <div style={{ border: `1px solid ${TH.headerBorder}`, borderRadius: 6, overflow: 'auto', minHeight: 0, background: '#FAFBFC' }}>
            {visibleDesigns.map((item) => (
              <label key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderBottom: `1px solid ${TH.statBorder}`, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={compareIds.includes(item.id)}
                  onChange={(event) => toggleComparison(item.id, event.target.checked)}
                />
                <span style={{ minWidth: 0 }}>
                  <b style={{ display: 'block', color: TH.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</b>
                </span>
              </label>
            ))}
          </div>
          <div style={{ overflow: 'auto', minHeight: 0, border: `1px solid ${TH.headerBorder}`, borderRadius: 6 }}>
            {comparedDesigns.length ? (
              <table style={{ width: '100%', minWidth:108+comparedDesigns.length*170, tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={compareMetricHead}>指标</th>
                    {comparedDesigns.map((item,index) => {const color=COMPARE_COLORS[index%COMPARE_COLORS.length];return <th key={item.id} title={item.name} style={{...comparePlanHead,background:color.head,color:color.text}}><span style={ellipsisOneLine}>{item.name}</span></th>})}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['方案评分', (item: Design) => {
                      const score = scoreFracPlan(item, evals, effectiveScoringConfig);
                      return `${score.score} 分 · ${score.label}`;
                    }],
                    ['段数', (item: Design) => `${item.stage_count} 段`],
                    ['平均每段簇数', (item: Design) => `${item.cluster_per_stage} 簇`],
                    ['平均段长', (item: Design) => `${item.stage_spacing} m`],
                    ['平均簇距', (item: Design) => `${item.cluster_spacing} m`],
                    ['排量', (item: Design) => `${item.pump_rate} m³/min`],
                    ['总液量', (item: Design) => `${item.total_fluid} m³`],
                    ['总砂量', (item: Design) => `${item.total_proppant} t`],
                    ['优化建议', (item: Design) => getOptimizationAdvice(item)],
                  ].map(([label, getValue],rowIndex) => {
                    const values=comparedDesigns.map(item=>(getValue as (value:Design)=>string)(item));const differs=new Set(values).size>1;const longText=String(label)==='优化建议';return (
                    <tr key={String(label)} style={{background:rowIndex%2?'#FAFBFC':'#fff'}}>
                      <td style={compareMetricCell}>{String(label)}</td>
                      {comparedDesigns.map((item,index) => {const color=COMPARE_COLORS[index%COMPARE_COLORS.length];return <td key={item.id} title={longText?undefined:values[index]} style={{...compareValueCell,background:differs?color.soft:undefined,fontWeight:IMPORTANT_COMPARE_ROWS.has(String(label))?700:500,verticalAlign:longText?'top':'middle'}}><span style={longText?fullAdviceText:ellipsisOneLine}>{values[index]}</span></td>})}
                    </tr>
                  )})}
                </tbody>
              </table>
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TH.textMuted }}>请选择需要对比的方案</div>
            )}
          </div>
        </div>
      </DraggableModal>

      {/* AI 顾问已提到 AppLayout 全局, 任何页面右下角都能用 */}
    </div>
  );
}

// Left canvas: TVD well path with MD-indexed interpreted formation intervals.
function StageCanvas({ layers, survey, stages, lateral, gradeAt, selStage, onSelectStage }: {
  layers: Layer[]; survey: Station[]; stages: Stage[];
  lateral: [number, number] | null; gradeAt: (md: number) => string; selStage: number | null;
  onSelectStage: (stageNumber: number | null) => void;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const stageHitsRef = useRef<Array<{ stageNumber: number; x1: number; y1: number; x2: number; y2: number; mx: number; my: number }>>([]);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => {
      const rect = canvas.getBoundingClientRect();
      setCanvasSize((current) => current.width === rect.width && current.height === rect.height
        ? current
        : { width: rect.width, height: rect.height });
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const rect = cv.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    cv.width = Math.round(rect.width * dpr); cv.height = Math.round(rect.height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const W = rect.width, H = rect.height;

    const maxTvd = survey.length ? Math.max(...survey.map((s) => s.tvd)) : 0;
    const vBot = maxTvd + Math.max(100, maxTvd * 0.03);
    const sY = (d: number) => (d / (vBot || 1)) * H;

    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);
    const trackL = 46, trackW = W - 56;

    const horiz: number[] = [0];
    for (let i = 1; i < survey.length; i++) {
      const a = survey[i - 1], b = survey[i];
      const hasCoordinates = a.ns != null && a.ew != null && b.ns != null && b.ew != null;
      const deltaHorizontal = hasCoordinates
        ? Math.hypot((b.ns as number) - (a.ns as number), (b.ew as number) - (a.ew as number))
        : (b.md - a.md) * Math.sin(((a.inc + b.inc) / 2) * Math.PI / 180);
      horiz[i] = horiz[i - 1] + deltaHorizontal;
    }
    const maxH = horiz.length ? horiz[horiz.length - 1] : 0;
    const cx = trackL + trackW * 0.26;
    const pxM = maxH > 5 ? Math.min((trackL + trackW - cx - 16) / maxH, 0.12) : 0;
    const xAt = (md: number) => {
      let i = survey.findIndex((s) => s.md >= md);
      if (i < 0) i = survey.length - 1;
      if (i === 0) return cx + horiz[0] * pxM;
      const a = survey[i - 1], b = survey[i];
      const t = b.md === a.md ? 0 : (md - a.md) / (b.md - a.md);
      return cx + (horiz[i - 1] + t * (horiz[i] - horiz[i - 1])) * pxM;
    };
    const yAt = (md: number) => {
      let i = survey.findIndex((s) => s.md >= md);
      if (i < 0) i = survey.length - 1;
      if (i === 0) return sY(survey[0]?.tvd ?? 0);
      const a = survey[i - 1], b = survey[i];
      const t = b.md === a.md ? 0 : (md - a.md) / (b.md - a.md);
      return sY(a.tvd + t * (b.tvd - a.tvd));
    };

    const drawMdInterval = (startMd: number, endMd: number) => {
      const mdValues = [
        startMd,
        ...survey.filter((station) => station.md > startMd && station.md < endMd).map((station) => station.md),
        endMd,
      ];
      ctx.beginPath();
      mdValues.forEach((md, index) => {
        const x = xAt(md), y = yAt(md);
        index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
    };

    const labelFontSize = Math.max(8, Math.min(11, W / 29));
    const formationBands = layers
      .map((layer) => {
        const topY = yAt(layer.top);
        const bottomY = yAt(layer.bottom);
        const bandTop = Math.min(topY, bottomY);
        const bandHeight = Math.max(1, Math.abs(bottomY - topY));
        return { layer, bandTop, bandHeight, rawLabelY: bandTop + bandHeight / 2, labelY: 0 };
      })
      .sort((a, b) => a.rawLabelY - b.rawLabelY);
    let previousLabelY = -Infinity;
    formationBands.forEach((band) => {
      band.labelY = Math.max(band.rawLabelY, previousLabelY + labelFontSize + 3);
      previousLabelY = band.labelY;
    });
    const overflow = Math.max(0, (formationBands[formationBands.length - 1]?.labelY ?? 0) - (H - 8));
    if (overflow) formationBands.forEach((band) => { band.labelY -= overflow; });

    formationBands.forEach(({ layer, bandTop, bandHeight, rawLabelY, labelY }) => {
      ctx.globalAlpha = layer.target ? 0.3 : 0.17;
      ctx.fillStyle = layer.color;
      ctx.fillRect(trackL, bandTop, trackW, bandHeight);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = layer.color;
      ctx.lineWidth = layer.target ? 1.3 : 0.7;
      ctx.beginPath(); ctx.moveTo(trackL, bandTop); ctx.lineTo(trackL + trackW, bandTop); ctx.stroke();

      const labelX = trackL + 5;
      ctx.strokeStyle = layer.color;
      ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(labelX, rawLabelY); ctx.lineTo(labelX, labelY); ctx.stroke();
      ctx.font = `${layer.target ? '800' : '700'} ${labelFontSize}px "Microsoft YaHei","PingFang SC","Noto Sans SC",sans-serif`;
      ctx.textAlign = 'left';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.strokeText(layer.name, labelX + 2, labelY + labelFontSize * 0.35);
      ctx.fillStyle = layer.target ? TH.target : TH.text;
      ctx.fillText(layer.name, labelX + 2, labelY + labelFontSize * 0.35);
    });

    if (survey.length > 1) {
      ctx.beginPath();
      survey.forEach((s, i) => {
        const x = cx + horiz[i] * pxM, y = sY(s.tvd);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.strokeStyle = 'rgba(44,62,80,0.3)'; ctx.lineWidth = 6; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
      ctx.strokeStyle = TH.accent; ctx.lineWidth = 2; ctx.stroke();
      ctx.lineCap = 'butt';
    } else {
      ctx.fillStyle = TH.textMuted;
      ctx.font = '11px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('当前项目尚未接入井轨迹数据', W / 2, H / 2);
      ctx.textAlign = 'left';
    }

    const stageHits: Array<{ stageNumber: number; x1: number; y1: number; x2: number; y2: number; mx: number; my: number }> = [];
    stages.forEach((s) => {
      const g = gradeAt((s.top_perf + s.bottom_perf) / 2);
      const gc = GRADE[g] || GRADE.III;
      const sel = s.stage_number === selStage;
      const x1 = xAt(s.top_perf), y1 = yAt(s.top_perf);
      const x2 = xAt(s.bottom_perf), y2 = yAt(s.bottom_perf);
      ctx.strokeStyle = gc.color; ctx.lineWidth = sel ? 9 : 6; ctx.globalAlpha = sel ? 0.95 : 0.7;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = gc.color; ctx.lineWidth = 1;
      const n = 5;
      for (let k = 0; k <= n; k++) {
        const t = k / n;
        const px = x1 + (x2 - x1) * t, py = y1 + (y2 - y1) * t;
        ctx.beginPath(); ctx.moveTo(px, py - 5); ctx.lineTo(px, py + 5); ctx.stroke();
      }
      const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
      ctx.fillStyle = '#fff'; ctx.strokeStyle = gc.color; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(mx, my - 12, 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = gc.color; ctx.font = 'bold 9px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(String(s.stage_number), mx, my - 9); ctx.textAlign = 'left';
      stageHits.push({ stageNumber: s.stage_number, x1, y1, x2, y2, mx, my: my - 12 });
    });
    stageHitsRef.current = stageHits;

    ctx.fillStyle = TH.textMuted; ctx.font = '10px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace';
    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    const st = vBot > 3000 ? 500 : 200;
    for (let d = 0; d <= vBot; d += st) {
      const y = sY(d); ctx.fillText(`${d}`, 4, y + 3);
      ctx.beginPath(); ctx.moveTo(trackL, y); ctx.lineTo(trackL + trackW, y); ctx.stroke();
    }
    ctx.save(); ctx.fillStyle = TH.textMuted; ctx.font = '10px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
    ctx.translate(11, H / 2); ctx.rotate(-Math.PI / 2); ctx.fillText('垂深 TVD (m)', -34, 0); ctx.restore();
  }, [canvasSize, layers, survey, stages, lateral, gradeAt, selStage]);

  return (
    <canvas
      ref={ref}
      title="点击压裂段可查看并设置对应段参数"
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const distanceToSegment = (hit: typeof stageHitsRef.current[number]) => {
          const dx = hit.x2 - hit.x1;
          const dy = hit.y2 - hit.y1;
          const denominator = dx * dx + dy * dy;
          const t = denominator ? Math.max(0, Math.min(1, ((x - hit.x1) * dx + (y - hit.y1) * dy) / denominator)) : 0;
          return Math.hypot(x - (hit.x1 + t * dx), y - (hit.y1 + t * dy));
        };
        const nearest = stageHitsRef.current
          .map((hit) => ({ hit, distance: Math.min(Math.hypot(x - hit.mx, y - hit.my), distanceToSegment(hit)) }))
          .sort((a, b) => a.distance - b.distance)[0];
        if (nearest && nearest.distance <= 12) {
          onSelectStage(nearest.hit.stageNumber === selStage ? null : nearest.hit.stageNumber);
        }
      }}
      style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}
    />
  );
}

function scheduleStepLabel(step: StageScheduleStep) {
  const name = step.step_type || step.step || '其他';
  return step.stage_index ? `${name} ${step.stage_index}级` : name;
}

// Retained experimental canvas implementation; the page uses the original view above.
function StageCanvasExperimental({ layers, survey, stages, lateral, gradeAt, selStage, onSelectStage }: {
  layers: Layer[]; survey: Station[]; stages: Stage[];
  lateral: [number, number] | null; gradeAt: (md: number) => string; selStage: number | null;
  onSelectStage: (stageNumber: number | null) => void;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const stageHitsRef = useRef<Array<{ x: number; y: number; stageNumber: number }>>([]);
  const dragRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [view, setView] = useState({ scale: 1, offsetX: 0, offsetY: 0 });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => {
      const rect = canvas.getBoundingClientRect();
      setCanvasSize((current) => current.width === rect.width && current.height === rect.height
        ? current
        : { width: rect.width, height: rect.height });
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.round(rect.width * dpr);
    cv.height = Math.round(rect.height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const W = rect.width;
    const H = rect.height;
    const trackL = 82;
    const trackW = Math.max(40, W - trackL - 10);

    const mdToTvd = (md: number) => {
      if (!survey.length) return md;
      let index = survey.findIndex((station) => station.md >= md);
      if (index < 0) index = survey.length - 1;
      if (index === 0) return survey[0].tvd;
      const start = survey[index - 1];
      const end = survey[index];
      const ratio = end.md === start.md ? 0 : (md - start.md) / (end.md - start.md);
      return start.tvd + ratio * (end.tvd - start.tvd);
    };
    const depthValues = [
      ...survey.map((station) => station.tvd),
      ...layers.flatMap((layer) => [mdToTvd(layer.top), mdToTvd(layer.bottom)]),
      ...stages.flatMap((stage) => [mdToTvd(stage.top_perf), mdToTvd(stage.bottom_perf)]),
      ...(lateral ? [mdToTvd(lateral[0]), mdToTvd(lateral[1])] : []),
    ].filter(Number.isFinite);
    const maxTvd = depthValues.length ? Math.max(...depthValues) : 1;
    const vBot = maxTvd + Math.max(80, maxTvd * 0.025);
    const baseY = (tvd: number) => (tvd / Math.max(vBot, 1)) * H;

    const horizontal: number[] = survey.length ? [0] : [];
    for (let index = 1; index < survey.length; index += 1) {
      const start = survey[index - 1];
      const end = survey[index];
      const hasCoordinates = start.ns != null && start.ew != null && end.ns != null && end.ew != null;
      const delta = hasCoordinates
        ? Math.hypot((end.ns as number) - (start.ns as number), (end.ew as number) - (start.ew as number))
        : (end.md - start.md) * Math.sin(((start.inc + end.inc) / 2) * Math.PI / 180);
      horizontal[index] = horizontal[index - 1] + delta;
    }
    const maxHorizontal = horizontal.length ? horizontal[horizontal.length - 1] : 0;
    const centerX = trackL + trackW * 0.22;
    const pxPerMeter = maxHorizontal > 5 ? Math.min((trackL + trackW - centerX - 12) / maxHorizontal, 0.13) : 0;
    const baseXAt = (md: number) => {
      if (!survey.length) return centerX;
      let index = survey.findIndex((station) => station.md >= md);
      if (index < 0) index = survey.length - 1;
      if (index === 0) return centerX + (horizontal[0] || 0) * pxPerMeter;
      const start = survey[index - 1];
      const end = survey[index];
      const ratio = end.md === start.md ? 0 : (md - start.md) / (end.md - start.md);
      return centerX + (horizontal[index - 1] + ratio * (horizontal[index] - horizontal[index - 1])) * pxPerMeter;
    };
    const screenX = (x: number) => W / 2 + (x - W / 2) * view.scale + view.offsetX;
    const screenY = (y: number) => H / 2 + (y - H / 2) * view.scale + view.offsetY;
    const xAt = (md: number) => screenX(baseXAt(md));
    const yAt = (md: number) => screenY(baseY(mdToTvd(md)));

    ctx.fillStyle = TH.canvasBg;
    ctx.fillRect(0, 0, W, H);

    // Full formation bands mapped from MD intervals to TVD. Labels are spread
    // vertically so thin adjacent formations remain readable.
    const layerBands = layers
      .map((layer) => {
        const topY = yAt(layer.top);
        const bottomY = yAt(layer.bottom);
        const bandTop = Math.min(topY, bottomY);
        const bandHeight = Math.max(1, Math.abs(bottomY - topY));
        return { layer, bandTop, bandHeight, rawLabelY: bandTop + bandHeight / 2, labelY: 0 };
      })
      .sort((a, b) => a.rawLabelY - b.rawLabelY);
    let previousLabelY = -Infinity;
    layerBands.forEach((band) => {
      band.labelY = Math.max(band.rawLabelY, previousLabelY + 15);
      previousLabelY = band.labelY;
    });
    const labelOverflow = Math.max(0, (layerBands[layerBands.length - 1]?.labelY ?? 0) - (H - 10));
    if (labelOverflow) layerBands.forEach((band) => { band.labelY -= labelOverflow; });

    layerBands.forEach(({ layer, bandTop, bandHeight, rawLabelY, labelY }) => {
      ctx.globalAlpha = layer.target ? 0.24 : 0.14;
      ctx.fillStyle = layer.color;
      ctx.fillRect(trackL, bandTop, W - trackL, bandHeight);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = layer.color;
      ctx.lineWidth = layer.target ? 1.5 : 0.8;
      ctx.beginPath();
      ctx.moveTo(trackL, bandTop);
      ctx.lineTo(W, bandTop);
      ctx.stroke();

      if (labelY > -12 && labelY < H + 12) {
        ctx.strokeStyle = layer.color;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(trackL - 3, rawLabelY);
        ctx.lineTo(trackL - 10, labelY);
        ctx.stroke();
        ctx.font = `${layer.target ? '800' : '700'} 10px "Microsoft YaHei","PingFang SC",sans-serif`;
        ctx.textAlign = 'left';
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(255,255,255,0.92)';
        ctx.strokeText(layer.name, 6, labelY + 3);
        ctx.fillStyle = layer.target ? TH.target : TH.text;
        ctx.fillText(layer.name, 6, labelY + 3);
      }
    });

    // TVD grid and fixed left depth scale.
    const depthStep = vBot > 3000 ? 500 : 200;
    ctx.font = '9px "Cascadia Code",Consolas,"Microsoft YaHei",monospace';
    for (let depth = 0; depth <= vBot; depth += depthStep) {
      const y = screenY(baseY(depth));
      if (y < -10 || y > H + 10) continue;
      ctx.strokeStyle = 'rgba(30,42,58,0.08)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(trackL, y);
      ctx.lineTo(W, y);
      ctx.stroke();
      ctx.fillStyle = TH.textMuted;
      ctx.textAlign = 'right';
      ctx.fillText(String(depth), trackL - 7, y + 3);
    }
    ctx.textAlign = 'right';
    ctx.fillStyle = TH.textMuted;
    ctx.font = 'bold 9px "Microsoft YaHei",sans-serif';
    ctx.fillText('TVD m', trackL - 7, 12);

    const drawMdInterval = (startMd: number, endMd: number) => {
      const values = [
        startMd,
        ...survey.filter((station) => station.md > startMd && station.md < endMd).map((station) => station.md),
        endMd,
      ];
      ctx.beginPath();
      values.forEach((md, index) => {
        const x = xAt(md);
        const y = yAt(md);
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
    };

    // Formation color sleeve along the borehole.
    layers.forEach((layer) => {
      const startMd = Math.max(layer.top, survey[0]?.md ?? layer.top);
      const endMd = Math.min(layer.bottom, survey[survey.length - 1]?.md ?? layer.bottom);
      if (endMd <= startMd) return;
      drawMdInterval(startMd, endMd);
      ctx.strokeStyle = layer.color;
      ctx.lineWidth = layer.target ? 15 : 11;
      ctx.globalAlpha = layer.target ? 0.72 : 0.5;
      ctx.lineCap = 'butt';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    if (survey.length > 1) {
      ctx.beginPath();
      survey.forEach((station, index) => {
        const x = screenX(centerX + horizontal[index] * pxPerMeter);
        const y = screenY(baseY(station.tvd));
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = 'rgba(44,62,80,0.35)';
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.strokeStyle = TH.accent;
      ctx.lineWidth = 2;
      ctx.stroke();
    } else {
      ctx.fillStyle = TH.textMuted;
      ctx.font = '11px "Microsoft YaHei",sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('当前项目尚未接入井轨迹数据', W / 2, H / 2);
    }

    const stageLabels = stages.map((stage) => {
      const grade = gradeAt((stage.top_perf + stage.bottom_perf) / 2);
      const gradeStyle = GRADE[grade] || GRADE.III;
      const selected = stage.stage_number === selStage;
      drawMdInterval(stage.top_perf, stage.bottom_perf);
      ctx.strokeStyle = gradeStyle.color;
      ctx.lineWidth = selected ? 10 : 7;
      ctx.globalAlpha = selected ? 1 : 0.82;
      ctx.stroke();
      ctx.globalAlpha = 1;

      const midMd = (stage.top_perf + stage.bottom_perf) / 2;
      return { stage, gradeStyle, selected, pathX: xAt(midMd), pathY: yAt(midMd) };
    }).sort((a, b) => a.pathX - b.pathX || b.stage.stage_number - a.stage.stage_number);

    const stageHits: Array<{ x: number; y: number; stageNumber: number }> = [];
    stageLabels.forEach(({ stage, gradeStyle, selected, pathX, pathY }, index) => {
      const usableWidth = Math.max(1, W - trackL - 30);
      const labelX = trackL + 15 + (stageLabels.length === 1 ? usableWidth / 2 : usableWidth * index / (stageLabels.length - 1));
      const labelY = Math.max(35, Math.min(H - 15, pathY - 16 - (index % 2) * 23));

      ctx.strokeStyle = gradeStyle.color;
      ctx.lineWidth = selected ? 1.8 : 0.8;
      ctx.globalAlpha = selected ? 0.95 : 0.55;
      ctx.beginPath();
      ctx.moveTo(pathX, pathY);
      ctx.lineTo(labelX, labelY + 9);
      ctx.stroke();
      ctx.globalAlpha = 1;

      if (selected) {
        ctx.fillStyle = gradeStyle.bg;
        ctx.beginPath();
        ctx.arc(labelX, labelY, 13, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = gradeStyle.color;
      ctx.beginPath();
      ctx.arc(labelX, labelY, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = '800 9px "Microsoft YaHei",sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(stage.stage_number), labelX, labelY + 3);
      stageHits.push({ x: labelX, y: labelY, stageNumber: stage.stage_number });
    });
    stageHitsRef.current = stageHits;

    // Sweet-spot color legend.
    const legend = Object.entries(GRADE);
    const legendWidth = 34 * legend.length + 8;
    const legendX = Math.max(trackL + 4, W - legendWidth - 8);
    ctx.fillStyle = 'rgba(255,255,255,0.88)';
    ctx.fillRect(legendX, 6, legendWidth, 20);
    legend.forEach(([key, item], index) => {
      const x = legendX + 7 + index * 34;
      ctx.fillStyle = item.color;
      ctx.fillRect(x, 12, 7, 7);
      ctx.fillStyle = TH.textSec;
      ctx.font = '9px "Microsoft YaHei",sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(key, x + 10, 19);
    });
  }, [canvasSize, gradeAt, lateral, layers, selStage, stages, survey, view]);

  const resetView = () => setView({ scale: 1, offsetX: 0, offsetY: 0 });
  const zoomBy = (factor: number) => setView((current) => ({
    ...current,
    scale: Math.max(0.7, Math.min(4, current.scale * factor)),
  }));

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <canvas
        ref={ref}
        onWheel={(event) => {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          const pointerX = event.clientX - rect.left;
          const pointerY = event.clientY - rect.top;
          setView((current) => {
            const nextScale = Math.max(0.7, Math.min(4, current.scale * Math.exp(-event.deltaY * 0.001)));
            const ratio = nextScale / current.scale;
            return {
              scale: nextScale,
              offsetX: pointerX - rect.width / 2 - (pointerX - rect.width / 2 - current.offsetX) * ratio,
              offsetY: pointerY - rect.height / 2 - (pointerY - rect.height / 2 - current.offsetY) * ratio,
            };
          });
        }}
        onDoubleClick={resetView}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          suppressClickRef.current = false;
          dragRef.current = { x: event.clientX, y: event.clientY, offsetX: view.offsetX, offsetY: view.offsetY, moved: false };
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          if (!drag) return;
          const deltaX = event.clientX - drag.x;
          const deltaY = event.clientY - drag.y;
          if (Math.abs(deltaX) + Math.abs(deltaY) > 3) drag.moved = true;
          setView((current) => ({ ...current, offsetX: drag.offsetX + deltaX, offsetY: drag.offsetY + deltaY }));
        }}
        onPointerUp={(event) => {
          const drag = dragRef.current;
          suppressClickRef.current = Boolean(drag?.moved);
          dragRef.current = null;
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={() => { dragRef.current = null; }}
        onClick={(event) => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          const rect = event.currentTarget.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;
          const hit = stageHitsRef.current.find((item) => Math.hypot(item.x - x, item.y - y) <= 14);
          if (hit) onSelectStage(hit.stageNumber === selStage ? null : hit.stageNumber);
        }}
        style={{ width: '100%', height: '100%', display: 'block', cursor: 'grab', touchAction: 'none' }}
      />
      <div style={{ position: 'absolute', right: 7, bottom: 7, display: 'flex', gap: 3, padding: 3, background: 'rgba(255,255,255,0.9)', border: `1px solid ${TH.headerBorder}`, borderRadius: 4 }}>
        <button type="button" title="放大" onClick={() => zoomBy(1.2)} style={{ width: 24, height: 22, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 800 }}>+</button>
        <button type="button" title="缩小" onClick={() => zoomBy(1 / 1.2)} style={{ width: 24, height: 22, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 800 }}>−</button>
        <button type="button" title="还原视图（也可双击画布）" onClick={resetView} style={{ height: 22, padding: '0 6px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 10 }}>1:1</button>
      </div>
    </div>
  );
}
